import * as NextSkyTheme from "@NextSkyTheme/global";
import { notifier } from "@NextSkyTheme/notification";
import { eventModal } from "@NextSkyTheme/modal";

const BOX_KEYS = ["medium", "large", "xlarge"];

function createBundleId() {
  return `byob-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getCartRoot() {
  return window.Shopify?.routes?.root || "/";
}

function buildCartAddUrl() {
  return window.routes?.cart_add_url || `${getCartRoot().replace(/\/?$/, "/")}cart/add`;
}

async function setByobCartAttribute(active) {
  const response = await fetch(`${getCartRoot().replace(/\/?$/, "/")}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      attributes: {
        byob_active: active ? "1" : "",
      },
    }),
  });
  return response.json();
}

function buildByobValidationBoxes(cartItems) {
  return (cartItems || [])
    .filter((item) => {
      const props = item.properties || {};
      return props._byob_role === "shell" || Boolean(props._byob_variant_ids);
    })
    .map((item) => {
      const props = item.properties || {};
      const names = buildVariantNameMapFromShellProps(props);

      return {
        t: props._byob_travel_ids || "",
        f: props._byob_full_ids || "",
        v: props._byob_variant_ids || "",
        d: props._byob_design_variant_id || "",
        ...(Object.keys(names).length ? { n: names } : {}),
      };
    });
}

function buildVariantNameMapFromShellProps(props, extraNames = {}) {
  const names = { ...extraNames };

  Object.entries(props || {}).forEach(([label]) => {
    if (label.startsWith("_") || label.toLowerCase().startsWith("byob_")) return;

    const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
    if (!skuMatch) return;

    const title = String(label)
      .replace(/\s*\([^)]+\)\s*$/, "")
      .trim();
    if (!title) return;

    names[`sku:${skuMatch[1].trim()}`] = title;
  });

  return names;
}

function buildByobNameMapFromState(state) {
  const names = {};

  [...(state?.travel || []), ...(state?.full || [])].forEach((item) => {
    if (item?.id && item?.title) {
      names[String(item.id)] = item.title;
    }
  });

  if (state?.boxDesign?.variantId && state?.boxDesign?.title) {
    names[String(state.boxDesign.variantId)] = state.boxDesign.title;
  }

  return names;
}

function buildByobImageMapFromState(state) {
  const images = {};

  [...(state?.travel || []), ...(state?.full || [])].forEach((item) => {
    if (item?.id && item?.image) {
      images[String(item.id)] = item.image;
    }
  });

  if (state?.boxDesign?.variantId && state?.boxDesign?.image) {
    images[String(state.boxDesign.variantId)] = state.boxDesign.image;
  }

  return images;
}

async function syncByobValidationCartAttribute(cartItems, extraNames = {}, extraImages = {}) {
  const boxes = buildByobValidationBoxes(cartItems).map((box) => {
    const mergedNames = { ...(box.n || {}), ...extraNames };
    const mergedImages = { ...(box.i || {}), ...extraImages };
    const next = { ...box };
    if (Object.keys(mergedNames).length) next.n = mergedNames;
    if (Object.keys(mergedImages).length) next.i = mergedImages;
    return next;
  });
  const response = await fetch(`${getCartRoot().replace(/\/?$/, "/")}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      attributes: {
        byob_active: boxes.length ? "1" : "",
        byob_validation: boxes.length ? JSON.stringify(boxes) : "",
        _byob_validation: "",
      },
    }),
  });
  return response.json();
}

class ByobBuilder extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.products = [];
    this.state = {
      boxKey: null,
      boxDesign: null,
      travel: [],
      full: [],
      step: 0,
    };
    this.steps = [];
    this.gridFilters = {
      travel: { sort: "popular", brand: "", productType: "", search: "" },
      full: { sort: "popular", brand: "", productType: "", search: "" },
    };
    this.isAddingToCart = false;
    this.returnToSummaryAfterEdit = false;
    this._stepperDotsCompleteThrough = -1;
    this.lastRemovedSelections = [];
    this.lastRemovedByKind = { travel: [], full: [] };
    this.lastRemovedDesign = null;
    this.stockAdjustedByKind = { travel: false, full: false, design: false };
  }

  connectedCallback() {
    this.config = JSON.parse(this.querySelector("[data-byob-config]")?.textContent || "{}");
    const productsScript = this.querySelector("[data-byob-products]");
    this.bootstrapProducts = productsScript
      ? JSON.parse(productsScript.textContent || "[]")
      : [];

    if (!this.config.collectionHandle && !this.bootstrapProducts.length) return;

    this.cart =
      document.querySelector("cart-drawer") || document.querySelector("main-cart");

    this.panels = {
      size: this.querySelector('[data-byob-panel="size"]'),
      design: this.querySelector('[data-byob-panel="design"]'),
      travel: this.querySelector('[data-byob-panel="travel"]'),
      full: this.querySelector('[data-byob-panel="full"]'),
      summary: this.querySelector('[data-byob-panel="summary"]'),
    };

    this.stepper = this.querySelector("[data-byob-stepper]");
    this.stepperSegments = this.querySelector("[data-byob-stepper-segments]");
    this.stepperDots = this.querySelector("[data-byob-stepper-dots]");
    this.main = this.querySelector(".byob-builder__main");
    this.stepTransition = this.querySelector("[data-byob-step-transition]");
    this.stepTransitionText = this.querySelector("[data-byob-step-transition-text]");
    this.isStepTransitioning = false;
    this._autoAdvanceActive = false;
    this.sidebar = this.querySelector("[data-byob-sidebar]");
    this.sidebarToggle = this.querySelector("[data-byob-sidebar-toggle]");
    this.nav = this.querySelector("[data-byob-nav]");
    this.mobileSidebarMq = window.matchMedia("(max-width: 1024.98px)");

    this.bindEvents();
    this.bindInventoryRefresh();
    this.syncMobileSidebarLayout();
    this.updateMobileSidebarHint(false);
    this.mobileSidebarMq.addEventListener("change", () => this.syncMobileSidebarLayout());

    if (this.hasPendingEditDraft()) {
      this.syncEditRestoreOverlay();
    }

    void this.initializeBuilder();
    this.updateLayoutMode();
  }

  hasPendingEditDraft() {
    try {
      return Boolean(sessionStorage.getItem("byob-edit-draft"));
    } catch {
      return false;
    }
  }

  getEditLoadingMessage() {
    return this.config?.strings?.editLoading || "Loading your box for editing...";
  }

  syncEditRestoreOverlay() {
    if (!this.stepTransition) return;

    this.isStepTransitioning = true;
    this._stepTransitionStart = Date.now();

    if (this.stepTransitionText) {
      this.stepTransitionText.textContent = this.getEditLoadingMessage();
    }

    document.body.classList.add("byob-step-transition-active");
    this.classList.add("is-step-transitioning", "byob-builder--edit-restoring");
    this.stepTransition.removeAttribute("hidden");
    this.stepTransition.classList.add("is-visible");
    this.stepTransition.setAttribute("aria-busy", "true");
  }

  beginEditRestoreOverlay() {
    if (this.isStepTransitioning) return;
    this.syncEditRestoreOverlay();
  }

  async initializeBuilder() {
    await this.loadProducts({ showOverlay: false });
    await this.tryRestoreEditDraft();
  }

  bindEvents() {
    this.querySelectorAll("[data-byob-box]").forEach((button) => {
      button.addEventListener("click", () => this.selectBox(button.dataset.byobBox));
    });

    this.querySelector("[data-byob-design-grid]")?.addEventListener("click", (event) => {
      const selectButton = event.target.closest("[data-byob-design-select]");
      if (!selectButton || selectButton.disabled) return;
      this.selectBoxDesign(Number(selectButton.dataset.byobDesignSelect));
    });

    this.querySelector("[data-byob-back]")?.addEventListener("click", () => this.goBack());
    this.querySelector("[data-byob-continue]")?.addEventListener("click", () => this.goNext());
    this.querySelector("[data-byob-add-to-cart]")?.addEventListener("click", () => this.addToCart());
    this.querySelector("[data-byob-add-to-cart-inline]")?.addEventListener("click", () => this.addToCart());

    this.querySelector("[data-byob-travel-search]")?.addEventListener("input", (event) => {
      this.gridFilters.travel.search = event.target.value;
      this.renderProductGrid("travel");
    });
    this.querySelector("[data-byob-full-search]")?.addEventListener("input", (event) => {
      this.gridFilters.full.search = event.target.value;
      this.renderProductGrid("full");
    });

    this.querySelectorAll("[data-byob-grid]").forEach((grid) => {
      grid.addEventListener("click", (event) => {
        const kind = grid.dataset.byobGrid;
        if (!kind) return;

        const addButton = event.target.closest("[data-byob-add-product]");
        if (addButton) {
          this.addProduct(kind, addButton.dataset.byobAddProduct);
          return;
        }

        const removeButton = event.target.closest("[data-byob-remove]");
        if (removeButton) {
          this.removeProduct(kind, removeButton.dataset.byobRemove);
        }
      });
    });

    this.querySelectorAll("[data-byob-filters]").forEach((container) => {
      container.addEventListener("change", (event) => {
        const kind = container.dataset.byobFilters;
        if (!kind) return;

        const filters = this.getGridFilters(kind);
        const target = event.target;

        if (target.matches("[data-byob-filter-sort]")) {
          filters.sort = target.value;
        } else if (target.matches("[data-byob-filter-brand]")) {
          filters.brand = target.value;
        } else if (target.matches("[data-byob-filter-type]")) {
          filters.productType = target.value;
        }

        this.renderProductGrid(kind);
      });
    });

    this.sidebarToggle?.addEventListener("click", () => this.toggleMobileSidebar());
  }

  bindInventoryRefresh() {
    this._onVisibilityChange = () => {
      if (document.hidden) return;

      const stepName = this.currentStepName();
      if (stepName !== "travel" && stepName !== "full" && stepName !== "summary") return;

      void this.refreshStepInventory(stepName === "summary" ? "travel" : stepName).then((removed) => {
        if (removed?.length) {
          notifier.show(this.formatRemovedProductsMessage(removed), "error", 6000);
        }
        if (stepName === "travel" || stepName === "full") {
          this.renderProductGrid(stepName);
        }
        this.renderSidebar();
        this.updateNavButtons();
        if (stepName === "summary") {
          this.renderSummary();
        }
      });
    };

    document.addEventListener("visibilitychange", this._onVisibilityChange);
  }

  disconnectedCallback() {
    if (this._onVisibilityChange) {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
    }
  }

  getRemainingVariantQuantity(variantId) {
    return Math.max(0, this.getAvailableQuantity(variantId) - this.countVariantInBox(variantId));
  }

  async refreshVariantInventory(variantId) {
    const numericId = Number(variantId);
    if (!Number.isFinite(numericId)) return;

    const root = this.getStoreRoot();
    const response = await fetch(`${root}variants/${numericId}.js`);
    if (!response.ok) return;

    const variant = await response.json();
    const product = this.getVariantProductRecord(numericId);
    if (!product?.variants) return;

    const index = product.variants.findIndex((entry) => Number(entry.id) === numericId);
    if (index < 0) return;

    product.variants[index] = { ...product.variants[index], ...variant };
  }

  async refreshStepInventory(kind) {
    const handles = new Set();

    this.getSelectionList(kind).forEach((item) => {
      const record = this.getVariantRecord(item.id);
      if (record?.product?.handle) handles.add(record.product.handle);
    });

    this.getFilteredProducts(kind).forEach((product) => {
      if (product?.handle) handles.add(product.handle);
    });

    if (!handles.size && !this.getSelectionList(kind).length) return [];

    await Promise.all([...handles].map((handle) => this.refreshProductInventory(handle)));
    await Promise.all(
      [...new Set(this.getSelectionList(kind).map((item) => item.id))].map((variantId) =>
        this.refreshVariantInventory(variantId),
      ),
    );

    return this.pruneInvalidSelections({ notify: false });
  }

  async refreshAllSelectionInventory({ notify = false } = {}) {
    await this.loadProducts({ showOverlay: false });

    const travelRemoved = (await this.refreshStepInventory("travel")) || [];
    const fullRemoved = (await this.refreshStepInventory("full")) || [];
    const removed = [...travelRemoved, ...fullRemoved];

    if (removed.length && notify) {
      notifier.show(this.formatRemovedProductsMessage(removed), "error", 6000);
    }

    return removed;
  }

  recordRemovedSelections(removed, { notify = false } = {}) {
    if (!removed.length) return;

    this.lastRemovedSelections = [...(this.lastRemovedSelections || []), ...removed];

    if (!this.lastRemovedByKind) {
      this.lastRemovedByKind = { travel: [], full: [] };
    }

    removed.forEach((item) => {
      if (!item?.kind) return;

      const exists = this.lastRemovedByKind[item.kind].some((entry) => entry.id === item.id);
      if (!exists) {
        this.lastRemovedByKind[item.kind].push(item);
      }

      if (item.kind === "travel" || item.kind === "full") {
        this.stockAdjustedByKind[item.kind] = true;
      }
    });

    if (notify) {
      notifier.show(this.formatRemovedProductsMessage(removed), "error", 6000);
    }
  }

  getRemovedForKind(kind) {
    return this.lastRemovedByKind?.[kind] || [];
  }

  clearRemovedForKind(kind) {
    if (!this.lastRemovedByKind?.[kind]?.length) return;

    this.lastRemovedByKind[kind] = [];
    this.lastRemovedSelections = (this.lastRemovedSelections || []).filter((item) => item.kind !== kind);
    this.stockAdjustedByKind[kind] = false;
  }

  resolveOneRemovedForKind(kind) {
    if (!this.lastRemovedByKind?.[kind]?.length) return;

    const [resolved] = this.lastRemovedByKind[kind].splice(0, 1);
    if (resolved?.id) {
      this.lastRemovedSelections = (this.lastRemovedSelections || []).filter((item) => item.id !== resolved.id);
    }
  }

  pruneInvalidSelections({ notify = false } = {}) {
    const removed = [];

    ["travel", "full"].forEach((kind) => {
      const list = this.getSelectionList(kind);
      const counts = new Map();

      list.forEach((item) => {
        counts.set(item.id, (counts.get(item.id) || 0) + 1);
      });

      counts.forEach((count, variantId) => {
        const available = this.getAvailableQuantity(variantId);
        let toRemove = count - available;
        if (toRemove <= 0) return;

        while (toRemove > 0) {
          const index = list.findLastIndex((item) => item.id === variantId);
          if (index < 0) break;

          removed.push({ ...list[index], kind });
          list.splice(index, 1);
          toRemove -= 1;
        }
      });
    });

    if (removed.length) {
      this.recordRemovedSelections(removed, { notify });
    }

    return removed;
  }

  formatRemovedProductsMessage(removed) {
    const names = [...new Set((removed || []).map((item) => item.title).filter(Boolean))];
    if (!names.length) return this.config.strings.selectionStockAdjusted || "";

    if (names.length === 1) {
      return String(
        this.config.strings.productRemovedSingle ||
          this.config.strings.productSoldOutSingle ||
          this.config.strings.selectionStockAdjusted ||
          "",
      ).replace(/\{\{\s*product\s*\}\}/g, names[0]);
    }

    return String(
      this.config.strings.productRemovedMultiple ||
        this.config.strings.productSoldOutMultiple ||
        this.config.strings.selectionStockAdjusted ||
        "",
    ).replace(/\{\{\s*products\s*\}\}/g, names.join(", "));
  }

  sectionHasStockContext(kind) {
    if (kind === "design") {
      return Boolean(this.lastRemovedDesign) || this.stockAdjustedByKind?.design;
    }

    return (
      this.getRemovedForKind(kind).length > 0 ||
      this.stockAdjustedByKind?.[kind] ||
      (this.lastRemovedSelections || []).some((item) => item.kind === kind)
    );
  }

  getSummarySectionMissingCount(kind) {
    if (kind === "design") {
      return this.hasBoxDesignStep() && !this.state.boxDesign?.variantId ? 1 : 0;
    }

    const box = this.getBoxConfig(this.state.boxKey);
    const required = kind === "travel" ? box?.travelCount || 0 : box?.fullCount || 0;
    const selected = this.getSelectionList(kind).length;
    const removed = this.getRemovedForKind(kind).length;

    return Math.max(0, required - selected - removed);
  }

  getSummarySectionNotice(kind) {
    if (kind === "design") {
      if (this.state.boxDesign?.variantId || !this.hasBoxDesignStep()) return "";

      if (this.sectionHasStockContext("design")) {
        return this.config.strings.summaryDesignNeedsReplacement || "";
      }

      return this.config.strings.summaryDesignMissing || "";
    }

    if (kind !== "travel" && kind !== "full") return "";

    const removed = this.getRemovedForKind(kind);
    const missing = this.getSummarySectionMissingCount(kind);

    if (missing <= 0 && removed.length === 0) return "";

    if (removed.length) {
      let message = this.formatRemovedProductsMessage(removed);
      if (missing > 0) {
        const pickReplacement = this.config.strings.summaryPickReplacement || "";
        if (pickReplacement) {
          message = `${message} ${pickReplacement}`;
        }
      }
      return message;
    }

    const count = String(missing);
    const hasStockContext = this.sectionHasStockContext(kind);

    if (hasStockContext) {
      const template =
        kind === "travel"
          ? this.config.strings.summaryTravelNeedsReplacement
          : this.config.strings.summaryFullNeedsReplacement;
      return String(template || "").replace(/\{\{\s*count\s*\}\}/g, count);
    }

    const template =
      kind === "travel" ? this.config.strings.summaryTravelMissing : this.config.strings.summaryFullMissing;
    return String(template || "").replace(/\{\{\s*count\s*\}\}/g, count);
  }

  getSummaryStockAlertMessage({ stockIssues = [], removed = [] } = {}) {
    if (stockIssues.length) {
      return this.formatStockIssuesMessage(stockIssues) || this.config.strings.summaryStockWarning;
    }

    if (removed.length) {
      return this.formatRemovedProductsMessage(removed);
    }

    return "";
  }

  formatStockIssuesMessage(issues) {
    if (!issues.length) return "";

    if (issues.length === 1) {
      const issue = issues[0];
      return String(this.config.strings.stockIssueSingle || "")
        .replace(/\{\{\s*product\s*\}\}/g, issue.title)
        .replace(/\{\{\s*available\s*\}\}/g, String(issue.available));
    }

    const names = issues.map((issue) => issue.title).join(", ");
    return String(this.config.strings.stockIssueMultiple || "").replace(/\{\{\s*products\s*\}\}/g, names);
  }

  isMobileSidebar() {
    return this.mobileSidebarMq?.matches ?? false;
  }

  syncMobileSidebarLayout() {
    if (!this.isMobileSidebar()) {
      this.classList.remove("byob-sidebar-expanded");
      this.sidebarToggle?.setAttribute("aria-expanded", "false");
      this.updateMobileSidebarHint(false);
    }
  }

  toggleMobileSidebar(forceExpanded) {
    if (!this.isMobileSidebar()) return;

    const expanded =
      typeof forceExpanded === "boolean"
        ? forceExpanded
        : !this.classList.contains("byob-sidebar-expanded");

    this.classList.toggle("byob-sidebar-expanded", expanded);
    this.sidebarToggle?.setAttribute("aria-expanded", String(expanded));
    this.updateMobileSidebarHint(expanded);
  }

  updateMobileSidebarHint(expanded = this.classList.contains("byob-sidebar-expanded")) {
    const hint = this.querySelector("[data-byob-sidebar-mobile-hint]");
    if (!hint || !this.isMobileSidebar()) return;

    hint.textContent = expanded
      ? this.config.strings.mobileCloseHint
      : this.config.strings.mobileOpenHint;
  }

  updateMobileDesignThumb() {
    const thumb = this.querySelector("[data-byob-sidebar-mobile-thumb]");
    const img = thumb?.querySelector("img");
    if (!thumb || !img) return;

    const design = this.state.boxDesign;
    if (this.hasBoxDesignStep() && design?.image) {
      img.src = design.image;
      img.alt = design.title || "";
      thumb.removeAttribute("hidden");
      return;
    }

    thumb.setAttribute("hidden", "");
    img.removeAttribute("src");
    img.alt = "";
  }

  formatMobileSelectionLine(kind, count, total) {
    return this.formatSizeProgress(kind, count, total);
  }

  getSizeLabel(kind, form = "short") {
    const labels = this.config.sizeLabels || {};
    const keys = {
      travel: { short: "travel", product: "travelProduct", products: "travelProducts" },
      full: { short: "full", product: "fullProduct", products: "fullProducts" },
    };
    const labelKey = keys[kind]?.[form];
    return (labelKey && labels[labelKey]) || kind;
  }

  formatSizeProgress(kind, count, total) {
    return `${count}/${total} ${this.getSizeLabel(kind, "products")} selected`;
  }

  formatPickSizeProductLabel(kind) {
    return `Pick a ${this.getSizeLabel(kind, "product")}`;
  }

  async loadProducts({ showOverlay = true } = {}) {
    if (this.productsLoadPromise) {
      return this.productsLoadPromise;
    }

    this.productsLoadPromise = this.fetchProducts({ showOverlay }).finally(() => {
      this.productsLoadPromise = null;
    });

    return this.productsLoadPromise;
  }

  async fetchProducts({ showOverlay = true } = {}) {
    const loading = this.querySelector("[data-byob-loading]");
    const useGlobalLoading = showOverlay && !this.isStepTransitioning && this.state.step > 0;
    if (useGlobalLoading) loading?.removeAttribute("hidden");

    try {
      this.products = (Array.isArray(this.bootstrapProducts) ? [...this.bootstrapProducts] : []).map(
        (product, index) => this.normalizeProduct(product, index),
      );

      const totalPages = Number(this.config.collectionPages) || 1;
      if (totalPages > 1 || !this.products.length) {
        const fetched = await this.fetchAllCollectionProducts(this.config.collectionHandle);
        if (fetched.length) {
          this.products = this.mergeProducts(this.products, fetched);
        }
      }
    } catch (error) {
      console.error(error);
      if (!this.products.length) {
        notifier.show(this.config.strings.loadError, "error", 4000);
      }
    } finally {
      if (useGlobalLoading) loading?.setAttribute("hidden", "");
    }

    const stepName = this.currentStepName();
    if (stepName === "travel" || stepName === "full") {
      this.renderFilterOptions(stepName);
      this.renderProductGrid(stepName);
    }
  }

  mergeProducts(existing, fetched) {
    const merged = [...existing];
    const byId = new Map(merged.map((product) => [product.id, product]));

    fetched.forEach((product) => {
      if (byId.has(product.id)) {
        const target = byId.get(product.id);
        if (!product.boxValue && target.boxValue) {
          product.boxValue = target.boxValue;
        }
        if (!product.originalPrice && target.originalPrice) {
          product.originalPrice = target.originalPrice;
        }
        if (product.valueAmount == null && target.valueAmount != null) {
          product.valueAmount = target.valueAmount;
        }
        if (product.priceAmount == null && target.priceAmount != null) {
          product.priceAmount = target.priceAmount;
        }
        if (product.compareAtAmount == null && target.compareAtAmount != null) {
          product.compareAtAmount = target.compareAtAmount;
        }
        const sourceType = this.getProductType(product);
        if (sourceType) {
          target.productType = sourceType;
        }
        if (product.popularityRank == null && target.popularityRank != null) {
          product.popularityRank = target.popularityRank;
        }
        if (product.isBestseller == null && target.isBestseller != null) {
          product.isBestseller = target.isBestseller;
        }
        this.syncProductVariants(target, product);
        this.enrichProductPricing(target);
      } else {
        const normalized = this.normalizeProduct(product, merged.length);
        merged.push(normalized);
        byId.set(product.id, normalized);
      }
    });

    return merged.map((product, index) => this.normalizeProduct(product, index));
  }

  normalizeProduct(product, index = 99999) {
    if (!product) return product;

    if (!product.productType) {
      product.productType = product.product_type || "";
    }

    if (product.popularityRank == null) {
      product.popularityRank = index;
    }

    if (product.isBestseller == null) {
      product.isBestseller = this.getProductTags(product).includes("bestseller");
    }

    return this.enrichProductPricing(product);
  }

  getProductType(product) {
    return String(product?.productType || product?.product_type || "").trim();
  }

  getGridFilters(kind) {
    if (!this.gridFilters[kind]) {
      this.gridFilters[kind] = { sort: "popular", brand: "", productType: "", search: "" };
    }

    return this.gridFilters[kind];
  }

  getProductValueLabel(product) {
    const boxValue = String(product.boxValue || "").trim();
    if (boxValue) return boxValue;

    let formatted = String(product.originalPrice || "").trim();
    if (!formatted) {
      const cents = this.getProductValueAmount(product);
      const moneyFormat = window.themeGlobalVariables?.settings?.money_format;
      if (cents > 0 && moneyFormat) {
        formatted = NextSkyTheme.formatMoney(cents, moneyFormat);
      }
    }

    if (!formatted) return "";

    const template = this.config.strings.badgeValue || "Value: {{ price }}";
    return template.replace(/\{\{\s*price\s*\}\}/g, formatted);
  }

  getProductValueAmount(product) {
    this.enrichProductPricing(product);
    return Number(product.valueAmount) || 0;
  }

  comparePopular(a, b) {
    const aBest = a.isBestseller ? 1 : 0;
    const bBest = b.isBestseller ? 1 : 0;
    if (bBest !== aBest) return bBest - aBest;

    const rankDiff = (a.popularityRank ?? 99999) - (b.popularityRank ?? 99999);
    if (rankDiff !== 0) return rankDiff;

    return String(a.title || "").localeCompare(String(b.title || ""));
  }

  sortProducts(products, sort) {
    const list = [...products];

    switch (sort) {
      case "value-desc":
        return list.sort((a, b) => {
          const diff = this.getProductValueAmount(b) - this.getProductValueAmount(a);
          return diff !== 0 ? diff : this.comparePopular(a, b);
        });
      case "value-asc":
        return list.sort((a, b) => {
          const diff = this.getProductValueAmount(a) - this.getProductValueAmount(b);
          return diff !== 0 ? diff : this.comparePopular(a, b);
        });
      case "popular":
      default:
        return list.sort((a, b) => this.comparePopular(a, b));
    }
  }

  getEligibleProducts(kind) {
    const tag = kind === "travel" ? this.config.travelTag : this.config.fullTag;

    return this.products.filter((product) => {
      if (!this.hasTag(product, tag)) return false;

      const variant = this.getFirstAvailableVariant(product);
      if (!variant?.id) return false;

      const inBox = this.countVariantInBox(variant.id) > 0;
      if (!inBox && !this.isVariantSelectable(variant)) return false;

      return true;
    });
  }

  renderFilterOptions(kind) {
    const container = this.querySelector(`[data-byob-filters="${kind}"]`);
    if (!container) return;

    const filters = this.getGridFilters(kind);
    const eligible = this.getEligibleProducts(kind);
    const brands = [...new Set(eligible.map((product) => product.vendor).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );
    const types = [
      ...new Set(eligible.map((product) => this.getProductType(product)).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b));

    if (filters.brand && !brands.includes(filters.brand)) {
      filters.brand = "";
    }

    if (filters.productType && !types.includes(filters.productType)) {
      filters.productType = "";
    }

    const strings = this.config.strings;

    container.innerHTML = `
      <div class="byob-builder__filters-grid">
        <div class="byob-builder__filter-field">
          <label class="byob-builder__filter-label" for="byob-sort-${kind}">${this.escapeHtml(strings.filterSortLabel)}</label>
          <div class="byob-builder__filter-control">
            <select id="byob-sort-${kind}" class="byob-builder__filter-select show-placehoder" data-byob-filter-sort>
              <option value="popular"${filters.sort === "popular" ? " selected" : ""}>${this.escapeHtml(strings.filterSortPopular)}</option>
              <option value="value-desc"${filters.sort === "value-desc" ? " selected" : ""}>${this.escapeHtml(strings.filterSortValueHigh)}</option>
              <option value="value-asc"${filters.sort === "value-asc" ? " selected" : ""}>${this.escapeHtml(strings.filterSortValueLow)}</option>
            </select>
          </div>
        </div>
        <div class="byob-builder__filter-field">
          <label class="byob-builder__filter-label" for="byob-brand-${kind}">${this.escapeHtml(strings.filterBrandLabel)}</label>
          <div class="byob-builder__filter-control">
            <select id="byob-brand-${kind}" class="byob-builder__filter-select show-placehoder" data-byob-filter-brand>
              <option value="">${this.escapeHtml(strings.filterBrandAll)}</option>
              ${brands
                .map(
                  (brand) => `
                    <option value="${this.escapeHtml(brand)}"${filters.brand === brand ? " selected" : ""}>
                      ${this.escapeHtml(brand)}
                    </option>
                  `,
                )
                .join("")}
            </select>
          </div>
        </div>
        <div class="byob-builder__filter-field">
          <label class="byob-builder__filter-label" for="byob-type-${kind}">${this.escapeHtml(strings.filterTypeLabel)}</label>
          <div class="byob-builder__filter-control">
            <select id="byob-type-${kind}" class="byob-builder__filter-select show-placehoder" data-byob-filter-type>
              <option value="">${this.escapeHtml(strings.filterTypeAll)}</option>
              ${types
                .map(
                  (type) => `
                    <option value="${this.escapeHtml(type)}"${filters.productType === type ? " selected" : ""}>
                      ${this.escapeHtml(type)}
                    </option>
                  `,
                )
                .join("")}
            </select>
          </div>
        </div>
      </div>
    `;
  }

  syncProductVariants(target, source) {
    if (!target || !source) return;

    if (!target.handle && source.handle) target.handle = source.handle;

    (source.variants || []).forEach((sourceVariant) => {
      const targetVariant = (target.variants || []).find(
        (variant) => Number(variant.id) === Number(sourceVariant.id),
      );

      if (!targetVariant) return;

      if (typeof sourceVariant.available === "boolean") {
        targetVariant.available = sourceVariant.available;
      }

      if (
        sourceVariant.inventory_quantity !== undefined &&
        sourceVariant.inventory_quantity !== null
      ) {
        targetVariant.inventory_quantity = sourceVariant.inventory_quantity;
      }

      if (sourceVariant.inventory_management) {
        targetVariant.inventory_management = sourceVariant.inventory_management;
      }

      if (sourceVariant.price !== undefined && sourceVariant.price !== null) {
        targetVariant.price = sourceVariant.price;
      }

      if (sourceVariant.compare_at_price !== undefined && sourceVariant.compare_at_price !== null) {
        targetVariant.compare_at_price = sourceVariant.compare_at_price;
      }
    });
  }

  parseMoneyToCents(value) {
    if (value == null || value === "") return 0;

    const raw = String(value).trim();
    if (!raw) return 0;

    if (raw.includes(".")) {
      const parsed = Number.parseFloat(raw);
      return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  enrichProductPricing(product) {
    if (!product) return product;

    const variant = this.getFirstAvailableVariant(product);
    if (!variant) return product;

    const priceCents = this.parseMoneyToCents(variant.price);
    const compareCents = this.parseMoneyToCents(variant.compare_at_price);
    const valueCents = compareCents > priceCents ? compareCents : priceCents;

    product.priceAmount = priceCents;
    product.compareAtAmount = compareCents;
    product.valueAmount = Math.max(priceCents, compareCents);

    if (!product.originalPrice && valueCents > 0) {
      const moneyFormat = window.themeGlobalVariables?.settings?.money_format;
      if (moneyFormat) {
        product.originalPrice = NextSkyTheme.formatMoney(valueCents, moneyFormat);
      }
    }

    return product;
  }

  getVariantRecord(variantId) {
    const numericId = Number(variantId);

    for (const product of this.products) {
      const variant = (product.variants || []).find((entry) => Number(entry.id) === numericId);
      if (variant) return { product, variant };
    }

    return null;
  }

  isInventoryTracked(variant) {
    return variant?.inventory_management === "shopify";
  }

  getAvailableQuantity(variantId) {
    const record = this.getVariantRecord(variantId);
    if (!record) return Number.MAX_SAFE_INTEGER;

    const { variant } = record;
    if (!variant.available) return 0;
    if (!this.isInventoryTracked(variant)) return Number.MAX_SAFE_INTEGER;

    const quantity = Number(variant.inventory_quantity);
    if (!Number.isFinite(quantity)) return Number.MAX_SAFE_INTEGER;

    return Math.max(0, quantity);
  }

  countVariantInBox(variantId) {
    const numericId = Number(variantId);
    return [...this.state.travel, ...this.state.full].filter((item) => item.id === numericId).length;
  }

  canAddVariant(variantId, kind) {
    const box = this.getBoxConfig(this.state.boxKey);
    if (!box) return false;

    const maxCount = kind === "travel" ? box.travelCount : box.fullCount;
    if (this.getSelectionList(kind).length >= maxCount) return false;

    return this.getRemainingVariantQuantity(variantId) > 0;
  }

  isVariantSelectable(variant) {
    if (!variant?.available) return false;
    if (!this.isInventoryTracked(variant)) return true;

    return this.getRemainingVariantQuantity(variant.id) > 0 || this.countVariantInBox(variant.id) > 0;
  }

  getStockHint(variant) {
    if (!variant || !this.isInventoryTracked(variant)) return "";

    const remaining = this.getRemainingVariantQuantity(variant.id);
    const inBox = this.countVariantInBox(variant.id);
    const displayCount = inBox > 0 ? remaining + inBox : remaining;

    if (displayCount <= 0) {
      return this.config.strings.productOutOfStock || "";
    }

    if (displayCount > 5) return "";

    return String(this.config.strings.lowStock || "").replace(/\{\{\s*count\s*\}\}/g, String(displayCount));
  }

  getStockIssues() {
    const issues = [];

    this.aggregateSelectionItems([...this.state.travel, ...this.state.full]).forEach((item) => {
      const record = this.getVariantRecord(item.id);
      if (!record) return;

      const { variant } = record;
      if (!variant.available) {
        issues.push({
          title: item.title,
          requested: item.quantity,
          available: 0,
        });
        return;
      }

      if (!this.isInventoryTracked(variant)) return;

      const quantity = Number(variant.inventory_quantity);
      if (!Number.isFinite(quantity)) return;

      if (item.quantity > quantity) {
        issues.push({
          title: item.title,
          requested: item.quantity,
          available: quantity,
        });
      }
    });

    return issues;
  }

  getFreeGift() {
    const gift = this.config?.freeGift;
    if (!gift?.variantId || gift.available === false) return null;
    return gift;
  }

  async postCartAdd({ items, sections = null }) {
    const config = NextSkyTheme.fetchConfig("json");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    const payload = { items };

    if (sections?.length) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }

    config.body = JSON.stringify(payload);

    const response = await fetch(buildCartAddUrl(), config);
    const text = await response.text();

    try {
      return JSON.parse(text);
    } catch (error) {
      console.error("Cart add returned non-JSON response", text.slice(0, 200));
      return {
        status: response.status || 500,
        description: this.config?.strings?.cartError || "Could not add to cart.",
      };
    }
  }

  async fetchCart() {
    const response = await fetch(`${getCartRoot().replace(/\/?$/, "/")}cart.js`, {
      headers: { Accept: "application/json" },
    });
    return response.json();
  }

  async waitForCartVariant(variantId, bundleId) {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const cart = await this.fetchCart();
      const found = cart.items?.some((item) => {
        if (Number(item.variant_id) !== Number(variantId)) return false;
        if (!bundleId || attempt >= 3) return true;
        return item.properties?._byob_bundle_id === bundleId;
      });
      if (found) return cart;
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
    return null;
  }

  async syncLocksmithCart() {
    if (typeof window.Locksmith?.ping === "function") {
      try {
        await Promise.race([
          window.Locksmith.ping(),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]);
      } catch (error) {
        console.warn("Locksmith ping failed", error);
      }
    }
  }

  getShellLineKey(cartAddResponse, variantId, bundleId) {
    const items = cartAddResponse?.items;
    if (Array.isArray(items)) {
      const match = items.find(
        (item) =>
          Number(item.variant_id || item.id) === Number(variantId) &&
          (!bundleId || item.properties?._byob_bundle_id === bundleId),
      );
      if (match?.key) return match.key;
    }
    return null;
  }

  async resolveShellLineKey(variantId, bundleId) {
    const cart = await this.fetchCart();
    const match = cart.items?.find(
      (item) =>
        Number(item.variant_id) === Number(variantId) &&
        item.properties?._byob_bundle_id === bundleId,
    );
    return match?.key || null;
  }

  buildGiftShellProperties(freeGift) {
    if (!freeGift?.variantId) return {};

    const properties = {};
    const giftLabel = this.config.strings.giftPropertyLabel || "Free gift";
    properties[giftLabel] = freeGift.title;
    properties._byob_gift_variant_id = String(freeGift.variantId);
    if (freeGift.sku) {
      properties._byob_gift_sku = freeGift.sku;
    }
    return properties;
  }

  async attachGiftPropertiesToShell(lineKey, shellProperties, freeGift) {
    if (!lineKey) return null;

    const response = await fetch(`${getCartRoot().replace(/\/?$/, "/")}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: lineKey,
        properties: {
          ...shellProperties,
          ...this.buildGiftShellProperties(freeGift),
        },
      }),
    });

    return response.json();
  }

  isSelectionComplete() {
    const box = this.getBoxConfig(this.state.boxKey);
    if (!box?.variantId) return false;

    if (this.hasBoxDesignStep() && !this.state.boxDesign?.variantId) return false;

    return (
      this.state.travel.length >= (box.travelCount || 0) &&
      this.state.full.length >= (box.fullCount || 0)
    );
  }

  async refreshSelectionInventory() {
    const handles = new Set();

    [...this.state.travel, ...this.state.full].forEach((item) => {
      const record = this.getVariantRecord(item.id);
      if (record?.product?.handle) handles.add(record.product.handle);
    });

    if (!handles.size) return;

    await Promise.all([...handles].map((handle) => this.refreshProductInventory(handle)));
  }

  async refreshProductInventory(handle) {
    const root = this.getStoreRoot();
    const response = await fetch(`${root}products/${handle}.js`);

    if (!response.ok) throw new Error(`Failed to refresh inventory for ${handle}`);

    const product = await response.json();
    const existing = this.products.find((entry) => Number(entry.id) === Number(product.id));

    if (existing) {
      this.syncProductVariants(existing, product);
      this.enrichProductPricing(existing);
      return;
    }

    this.products.push(product);
  }

  async fetchAllCollectionProducts(handle) {
    const products = [];
    let page = 1;
    const root = this.getStoreRoot();

    while (true) {
      const response = await fetch(
        `${root}collections/${handle}/products.json?limit=250&page=${page}`,
      );
      if (!response.ok) throw new Error("Failed to load collection products");

      const data = await response.json();
      products.push(...(data.products || []));

      if (!data.products || data.products.length < 250) break;
      page += 1;
    }

    return products;
  }

  getStoreRoot() {
    const configuredRoot = this.config.storeRoot || window.Shopify?.routes?.root || "/";
    if (configuredRoot === "/") return "/";
    return configuredRoot.endsWith("/") ? configuredRoot : `${configuredRoot}/`;
  }

  hasTag(product, tagName) {
    const needles = this.getTagNeedles(tagName);
    const tags = this.getProductTags(product);
    return tags.some((tag) => needles.includes(tag.trim().toLowerCase()));
  }

  getTagNeedles(tagName) {
    const needles = String(tagName || "")
      .split(/[,|]/)
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);

    if (needles.includes("full size") && !needles.includes("full")) {
      needles.push("full");
    }
    if (needles.includes("full") && !needles.includes("full size")) {
      needles.push("full size");
    }

    return needles;
  }

  getProductTags(product) {
    const tags = product?.tags;
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }

  isProductAvailable(product) {
    return (product.variants || []).some((variant) => this.isVariantSelectable(variant));
  }

  getBoxDesigns() {
    return Array.isArray(this.config.boxDesigns) ? this.config.boxDesigns : [];
  }

  hasBoxDesignStep() {
    return this.getBoxDesigns().length > 0;
  }

  getBoxConfig(boxKey) {
    return this.config.boxes?.[boxKey] || null;
  }

  resolveBoxKeyFromShellVariantId(shellVariantId) {
    const numericId = Number(shellVariantId);
    if (!Number.isFinite(numericId)) return null;

    return (
      BOX_KEYS.find((boxKey) => Number(this.getBoxConfig(boxKey)?.variantId) === numericId) || null
    );
  }

  parseVariantIdList(value) {
    return String(value || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  getVariantProductRecord(variantId) {
    const numericId = Number(variantId);
    return this.products.find((product) =>
      (product.variants || []).some((variant) => Number(variant.id) === numericId),
    );
  }

  classifyVariantIds(variantIds, designVariantId = null) {
    const travelIds = [];
    const fullIds = [];
    const designId = Number(designVariantId);

    variantIds.forEach((variantId) => {
      const numericId = Number(variantId);
      if (designId && numericId === designId) return;

      const product = this.getVariantProductRecord(numericId);
      if (!product) return;

      if (this.hasTag(product, this.config.travelTag)) {
        travelIds.push(numericId);
        return;
      }

      if (this.hasTag(product, this.config.fullTag)) {
        fullIds.push(numericId);
      }
    });

    return { travelIds, fullIds };
  }

  buildSelectionItemsFromIds(variantIds) {
    return variantIds.map((variantId) => this.getProductVariant(variantId)).filter(Boolean);
  }

  findVariantIdBySku(sku) {
    const needle = String(sku || "").trim();
    if (!needle) return null;

    for (const product of this.products) {
      const variant = (product.variants || []).find((entry) => String(entry.sku || "").trim() === needle);
      if (variant?.id) return Number(variant.id);
    }

    return null;
  }

  resolveVariantIdsFromVisibleProperties(properties) {
    const ids = [];

    properties.forEach(({ label, value }) => {
      const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
      const qtyMatch = String(value).match(/x(\d+)/i);
      const quantity = qtyMatch ? Number(qtyMatch[1]) : 1;
      const sku = skuMatch ? skuMatch[1].trim() : null;
      if (!sku || !Number.isFinite(quantity) || quantity < 1) return;

      const variantId = this.findVariantIdBySku(sku);
      if (!variantId) return;

      for (let index = 0; index < quantity; index += 1) {
        ids.push(variantId);
      }
    });

    return ids;
  }

  resolveDesignVariantIdFromVisibleProperties(properties) {
    const designs = this.getBoxDesigns();
    if (!designs.length) return null;

    for (const { label } of properties) {
      const skuMatch = String(label).match(/\(([^)]+)\)\s*$/);
      const sku = skuMatch ? skuMatch[1].trim() : null;
      if (!sku) continue;

      const design = designs.find((entry) => String(entry.sku || "").trim() === sku);
      if (design?.variantId) return Number(design.variantId);
    }

    return null;
  }

  async tryRestoreEditDraft() {
    const raw = sessionStorage.getItem("byob-edit-draft");
    if (!raw) return false;

    const startedHere = !this.isStepTransitioning;
    if (startedHere) {
      this.beginEditRestoreOverlay();
    } else if (!this._stepTransitionStart) {
      this._stepTransitionStart = Date.now();
    }

    let draft;
    try {
      draft = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem("byob-edit-draft");
      return false;
    }

    sessionStorage.removeItem("byob-edit-draft");

    let restored = false;
    try {
      restored = await this.applyEditDraft(draft);
    } finally {
      this.classList.remove("byob-builder--edit-restoring");
      if (this.isStepTransitioning) {
        await this.endStepTransition();
      }
    }

    if (restored) {
      notifier.show(this.config.strings.editRestored, "success", 4000);
      return true;
    }

    notifier.show(this.config.strings.editRestoreError, "error", 5000);
    return false;
  }

  async applyEditDraft(draft) {
    if (!draft?.lineKey) return false;

    let boxKey = draft.boxKey || this.resolveBoxKeyFromShellVariantId(draft.shellVariantId);
    if (!boxKey || !this.getBoxConfig(boxKey)?.variantId) return false;

    let travelIds = Array.isArray(draft.travelIds) ? draft.travelIds : this.parseVariantIdList(draft.travelIds);
    let fullIds = Array.isArray(draft.fullIds) ? draft.fullIds : this.parseVariantIdList(draft.fullIds);
    let designVariantId = draft.designVariantId || null;

    if (!travelIds.length && !fullIds.length) {
      const fallbackIds = Array.isArray(draft.variantIds)
        ? draft.variantIds
        : this.parseVariantIdList(draft.variantIds);

      if (fallbackIds.length) {
        const classified = this.classifyVariantIds(fallbackIds, designVariantId);
        travelIds = classified.travelIds;
        fullIds = classified.fullIds;
      } else if (Array.isArray(draft.visibleProperties) && draft.visibleProperties.length) {
        if (!designVariantId) {
          designVariantId = this.resolveDesignVariantIdFromVisibleProperties(draft.visibleProperties);
        }

        const resolvedIds = this.resolveVariantIdsFromVisibleProperties(draft.visibleProperties);
        const classified = this.classifyVariantIds(resolvedIds, designVariantId);
        travelIds = classified.travelIds;
        fullIds = classified.fullIds;
      }
    }

    await this.removeCartLineByKey(draft.lineKey, { silent: true });

    this.state.boxKey = boxKey;
    this.state.travel = this.buildSelectionItemsFromIds(travelIds);
    this.state.full = this.buildSelectionItemsFromIds(fullIds);
    this.state.boxDesign = null;

    if (designVariantId) {
      const design = this.getBoxDesigns().find(
        (entry) => Number(entry.variantId) === Number(designVariantId),
      );
      if (design?.variantId) {
        this.state.boxDesign = {
          variantId: Number(design.variantId),
          productId: design.productId,
          title: design.title,
          sku: design.sku || "",
          image: design.image || "",
        };
      }
    }

    this.buildSteps();
    this.querySelectorAll("[data-byob-box]").forEach((button) => {
      button.classList.toggle("is-selected", button.dataset.byobBox === boxKey);
    });

    const summaryIndex = this.steps.indexOf("summary");
    if (summaryIndex < 0) return false;

    await this.pruneInvalidSelections({ notify: true });

    if (draft.designVariantId && !this.state.boxDesign?.variantId) {
      const design = this.getBoxDesigns().find(
        (entry) => Number(entry.variantId) === Number(draft.designVariantId),
      );
      if (design?.variantId) {
        this.lastRemovedDesign = {
          title: design.title,
          image: design.image || "",
          variantId: design.variantId,
        };
        this.stockAdjustedByKind.design = true;
      }
    }

    await this.goToStep(summaryIndex, { skipBeginTransition: true });
    return this.isSelectionComplete();
  }

  async removeCartLineByKey(lineKey, { silent = false } = {}) {
    if (!lineKey) return true;

    if (typeof window.byobRemoveBundleItems === "function") {
      try {
        return await window.byobRemoveBundleItems(lineKey);
      } catch {
        if (!silent) {
          notifier.show(this.config.strings.cartError, "error", 4000);
        }
        return false;
      }
    }

    const config = NextSkyTheme.fetchConfig("json");
    config.body = JSON.stringify({
      id: lineKey,
      quantity: 0,
    });

    try {
      const response = await fetch(window.routes.cart_change_url, config);
      const data = await response.json();

      if (data?.status) {
        if (!silent) {
          notifier.show(data.description || this.config.strings.cartError, "error", 4000);
        }
        return false;
      }

      return true;
    } catch {
      return silent;
    }
  }

  selectBox(boxKey) {
    if (!BOX_KEYS.includes(boxKey) || !this.getBoxConfig(boxKey)?.variantId) return;

    this.lastRemovedSelections = [];
    this.lastRemovedByKind = { travel: [], full: [] };
    this.lastRemovedDesign = null;
    this.stockAdjustedByKind = { travel: false, full: false, design: false };
    this.state.boxKey = boxKey;
    this.state.boxDesign = null;
    this.state.travel = [];
    this.state.full = [];
    this.returnToSummaryAfterEdit = false;
    this.gridFilters = {
      travel: { sort: "popular", brand: "", productType: "", search: "" },
      full: { sort: "popular", brand: "", productType: "", search: "" },
    };
    const travelSearch = this.querySelector("[data-byob-travel-search]");
    const fullSearch = this.querySelector("[data-byob-full-search]");
    if (travelSearch) travelSearch.value = "";
    if (fullSearch) fullSearch.value = "";
    this.buildSteps();
    void this.goToStep(1);

    this.querySelectorAll("[data-byob-box]").forEach((button) => {
      button.classList.toggle("is-selected", button.dataset.byobBox === boxKey);
    });
  }

  buildSteps() {
    const box = this.getBoxConfig(this.state.boxKey);
    this.steps = ["size"];

    if (this.hasBoxDesignStep()) this.steps.push("design");
    if ((box.travelCount || 0) > 0) this.steps.push("travel");
    if ((box.fullCount || 0) > 0) this.steps.push("full");
    this.steps.push("summary");

    this.renderStepper();
  }

  selectBoxDesign(variantId) {
    const design = this.getBoxDesigns().find((entry) => Number(entry.variantId) === Number(variantId));
    if (!design?.variantId || design.available === false) return;

    this.state.boxDesign = {
      variantId: Number(design.variantId),
      productId: design.productId,
      title: design.title,
      sku: design.sku || "",
      image: design.image || "",
    };
    this.lastRemovedDesign = null;
    this.stockAdjustedByKind.design = false;

    this.renderDesignGrid();
    this.renderSidebar();
    this.updateNavButtons();
    this.maybeAutoAdvance("design");
  }

  getDesignGridColumns(count) {
    if (count <= 1) {
      return { mobile: 1, desktop: 1 };
    }

    if (count === 2 || count === 4) {
      return { mobile: 2, desktop: 2 };
    }

    return {
      mobile: Math.min(count, 2),
      desktop: Math.min(count, 3),
    };
  }

  getDesignControlsHtml(design, isSelected) {
    const isUnavailable = design.available === false;

    if (isUnavailable) {
      return `<p class="byob-builder__product-unavailable mb-0">${this.escapeHtml(this.config.strings.outOfStock)}</p>`;
    }

    if (isSelected) {
      return `
        <button
          type="button"
          class="btn btn-primary w-full byob-builder__add-product-btn"
          disabled
          aria-pressed="true"
        >
          ${this.escapeHtml(this.config.strings.selected)}
        </button>
      `;
    }

    return `
      <button
        type="button"
        class="btn btn-primary w-full byob-builder__add-product-btn"
        data-byob-design-select="${design.variantId}"
        aria-label="${this.escapeHtml(this.config.strings.select)} ${this.escapeHtml(design.title)}"
      >
        ${this.escapeHtml(this.config.strings.select)}
      </button>
    `;
  }

  renderDesignGrid() {
    const grid = this.querySelector("[data-byob-design-grid]");
    if (!grid) return;

    const designs = this.getBoxDesigns();
    const selectedId = this.state.boxDesign?.variantId;
    const { mobile, desktop } = this.getDesignGridColumns(designs.length);

    grid.style.setProperty("--byob-design-cols-mobile", String(mobile));
    grid.style.setProperty("--byob-design-cols-desktop", String(desktop));
    grid.classList.toggle("byob-builder__design-grid--few", designs.length <= 2);

    grid.innerHTML = designs
      .map((design) => {
        const isSelected = Number(design.variantId) === Number(selectedId);
        const isUnavailable = design.available === false;
        const imageHtml = design.image
          ? `<img src="${this.escapeHtml(design.image)}" alt="${this.escapeHtml(design.title)}" loading="lazy" width="640" height="800">`
          : "";

        return `
          <article
            class="byob-builder__product-card byob-builder__design-card${isSelected ? " is-selected" : ""}${isUnavailable ? " is-unavailable" : ""}"
            data-byob-design="${design.variantId}"
          >
            <div class="byob-builder__product-media">
              ${imageHtml}
              ${isSelected ? this.getSelectedBadgeHtml() : ""}
            </div>
            <div class="byob-builder__product-body">
              <p class="byob-builder__product-title">${this.escapeHtml(design.title)}</p>
              <div data-byob-design-controls>${this.getDesignControlsHtml(design, isSelected)}</div>
            </div>
          </article>
        `;
      })
      .join("");
  }

  prepareStepperAdvance(previousStep) {
    if (this.state.step > previousStep) {
      this._stepperDotsCompleteThrough = previousStep;
      return;
    }

    this._stepperDotsCompleteThrough = this.state.step - 1;
  }

  isStepperDotComplete(index) {
    return index <= this._stepperDotsCompleteThrough;
  }

  isStepperStepActive(index) {
    return index === this.state.step;
  }

  getStepperStepClassName(index) {
    const classes = ["byob-builder__stepper-step"];
    if (this.isStepperDotComplete(index)) classes.push("is-complete");
    else if (this.isStepperStepActive(index)) classes.push("is-active");
    return classes.join(" ");
  }

  refreshStepperDots() {
    if (!this.stepperDots) return;

    this.stepperDots.querySelectorAll("[data-byob-stepper-step]").forEach((stepEl) => {
      const index = Number(stepEl.dataset.byobStepperStep);
      stepEl.className = this.getStepperStepClassName(index);
      const dot = stepEl.querySelector(".byob-builder__stepper-dot");
      if (dot) dot.innerHTML = this.renderStepperDotContent(index);
    });
  }

  handleStepperSegmentFilled(segmentIndex) {
    if (segmentIndex !== this.state.step - 1) return;
    if (this._stepperDotsCompleteThrough >= segmentIndex) return;

    this._stepperDotsCompleteThrough = segmentIndex;
    this.refreshStepperDots();
  }

  renderStepperSegmentMarkup(index, stepCount) {
    const start = this.getStepperStepLeft(index, stepCount);
    const end = this.getStepperStepLeft(index + 1, stepCount);

    return `
      <span
        class="byob-builder__stepper-segment"
        data-byob-stepper-segment="${index}"
        style="--byob-segment-start: ${start}; --byob-segment-end: ${end};"
      >
        <span class="byob-builder__stepper-segment-fill"></span>
      </span>
    `;
  }

  renderStepperSegments(stepCount) {
    const container = this.stepperSegments;
    if (!container) return;

    if (stepCount < 2) {
      container.innerHTML = "";
      return;
    }

    const existing = container.querySelectorAll("[data-byob-stepper-segment]");
    const needsRebuild = existing.length !== stepCount - 1;

    if (needsRebuild) {
      container.innerHTML = Array.from({ length: stepCount - 1 }, (_, index) =>
        this.renderStepperSegmentMarkup(index, stepCount),
      ).join("");
      container.classList.add("is-instant");
      this._stepperDotsCompleteThrough = this.state.step - 1;
      this.updateStepperSegmentCompleteState(stepCount, { instant: true });
      requestAnimationFrame(() => {
        container.classList.remove("is-instant");
      });
      return;
    }

    existing.forEach((segment, index) => {
      segment.style.setProperty("--byob-segment-start", this.getStepperStepLeft(index, stepCount));
      segment.style.setProperty(
        "--byob-segment-end",
        this.getStepperStepLeft(index + 1, stepCount),
      );
    });

    this.updateStepperSegmentCompleteState(stepCount);
  }

  updateStepperSegmentCompleteState(stepCount, { instant = false } = {}) {
    const container = this.stepperSegments;
    if (!container) return;

    container.querySelectorAll("[data-byob-stepper-segment]").forEach((segment, index) => {
      const fill = segment.querySelector(".byob-builder__stepper-segment-fill");
      const onFilled = (event) => {
        if (event.propertyName !== "transform" && event.propertyName !== "webkitTransform") return;
        this.handleStepperSegmentFilled(index);
      };

      fill?.removeEventListener("transitionend", segment._byobFillHandler);
      segment._byobFillHandler = onFilled;

      if (index < this.state.step - 1 || (instant && index < this.state.step)) {
        segment.classList.add("is-complete");
        fill?.removeEventListener("transitionend", onFilled);
        return;
      }

      if (!instant && index === this.state.step - 1) {
        segment.classList.remove("is-complete");
        const completeDot = () => this.handleStepperSegmentFilled(index);
        fill?.addEventListener("transitionend", onFilled, { once: true });
        window.setTimeout(completeDot, this.isMobileSidebar() ? 240 : 420);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            segment.classList.add("is-complete");
          });
        });
        return;
      }

      segment.classList.remove("is-complete");
      fill?.removeEventListener("transitionend", onFilled);
    });
  }

  renderStepperCheckIcon() {
    return `<svg class="byob-builder__stepper-check" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  renderStepperDotContent(index) {
    return this.isStepperDotComplete(index) ? this.renderStepperCheckIcon() : String(index + 1);
  }

  getStepperStepLeft(index, stepCount) {
    if (stepCount <= 1) return "50%";

    if (index === 0) return "calc(var(--byob-step-dot) / 2)";
    if (index === stepCount - 1) return "calc(100% - var(--byob-step-dot) / 2)";

    const ratio = index / (stepCount - 1);
    return `calc(var(--byob-step-dot) / 2 + (100% - var(--byob-step-dot)) * ${ratio})`;
  }

  renderStepper() {
    if (!this.stepper || !this.stepperDots) return;

    const labels = this.steps.map((step) => this.config.strings.steps[step] || step);
    const stepCount = this.steps.length;

    this.stepperDots.innerHTML = labels
      .map(
        (label, index) => `
          <span
            class="${this.getStepperStepClassName(index)}"
            data-byob-stepper-step="${index}"
            style="left: ${this.getStepperStepLeft(index, stepCount)}"
          >
            <span class="byob-builder__stepper-dot" aria-hidden="true">
              ${this.renderStepperDotContent(index)}
            </span>
            <span class="byob-builder__stepper-label">${this.escapeHtml(label)}</span>
          </span>
        `,
      )
      .join("");

    this.stepper.style.setProperty("--byob-step-count", String(stepCount));
    this.renderStepperSegments(stepCount);

    this.stepper.removeAttribute("hidden");
  }

  currentStepName() {
    return this.steps[this.state.step] || "size";
  }

  updateLayoutMode() {
    const isSizeStep = this.state.step === 0 || this.currentStepName() === "size";
    this.classList.toggle("byob-builder--size-step", isSizeStep);
  }

  getStepIndex(stepName) {
    return this.steps.indexOf(stepName);
  }

  isStepComplete(stepName) {
    const box = this.getBoxConfig(this.state.boxKey);
    if (!box) return false;

    if (stepName === "design") {
      if (!this.hasBoxDesignStep()) return true;
      return Boolean(this.state.boxDesign?.variantId);
    }

    if (stepName === "travel") {
      const required = box.travelCount || 0;
      if (!required) return true;
      return this.state.travel.length >= required;
    }

    if (stepName === "full") {
      const required = box.fullCount || 0;
      if (!required) return true;
      return this.state.full.length >= required;
    }

    return true;
  }

  getNextStepIndexAfterComplete(currentKind) {
    const currentIndex =
      typeof currentKind === "number" ? currentKind : this.getStepIndex(currentKind);

    if (currentIndex < 0) {
      return Math.min(this.state.step + 1, this.steps.length - 1);
    }

    if (!this.returnToSummaryAfterEdit) {
      return Math.min(currentIndex + 1, this.steps.length - 1);
    }

    for (let index = currentIndex + 1; index < this.steps.length; index += 1) {
      const stepName = this.steps[index];
      if (stepName === "summary") return index;
      if (!this.isStepComplete(stepName)) return index;
    }

    const summaryIndex = this.getStepIndex("summary");
    return summaryIndex >= 0 ? summaryIndex : this.steps.length - 1;
  }

  goToPickStep(kind) {
    const stepIndex = this.getStepIndex(kind);
    if (stepIndex < 0) return;
    this.returnToSummaryAfterEdit = this.currentStepName() === "summary";
    void this.goToStep(stepIndex);
  }

  wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  getStepTransitionMessage(stepName) {
    const messages = {
      size: this.config.strings.stepLoadingSize,
      design: this.config.strings.stepLoadingDesign,
      travel: this.config.strings.stepLoadingTravel,
      full: this.config.strings.stepLoadingFull,
      summary: this.config.strings.stepLoadingSummary,
    };

    return messages[stepName] || this.config.strings.stepLoading || this.config.strings.loading;
  }

  async beginStepTransition(message) {
    if (!this.stepTransition) return;

    this.isStepTransitioning = true;
    this._stepTransitionStart = Date.now();

    if (this.stepTransitionText) {
      this.stepTransitionText.textContent = message || "";
    }

    document.body.classList.add("byob-step-transition-active");
    this.classList.add("is-step-transitioning");
    this.stepTransition.removeAttribute("hidden");
    this.stepTransition.setAttribute("aria-busy", "true");

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    this.stepTransition.classList.add("is-visible");
  }

  async abortStepTransition() {
    if (!this.stepTransition || !this.isStepTransitioning) return;

    this.stepTransition.classList.remove("is-visible");
    this.stepTransition.setAttribute("aria-busy", "false");
    this.stepTransition.setAttribute("hidden", "");
    document.body.classList.remove("byob-step-transition-active");
    this.classList.remove("is-step-transitioning");
    this.isStepTransitioning = false;
    this._stepTransitionStart = 0;
  }

  clearAutoAdvanceTimers() {
    if (this._autoAdvanceTimeout) {
      window.clearTimeout(this._autoAdvanceTimeout);
      this._autoAdvanceTimeout = null;
    }

    if (this._autoAdvanceStepTimeout) {
      window.clearTimeout(this._autoAdvanceStepTimeout);
      this._autoAdvanceStepTimeout = null;
    }
  }

  abortAutoAdvance() {
    this.clearAutoAdvanceTimers();
    this._autoAdvanceActive = false;
    void this.abortStepTransition();
  }

  isAutoAdvanceStillValid(kind, box) {
    if (this.currentStepName() !== kind) return false;
    if (this.state.step >= this.steps.length - 1) return false;

    if (kind === "design") {
      return Boolean(this.state.boxDesign?.variantId);
    }

    if (kind === "travel" || kind === "full") {
      const required = kind === "travel" ? box.travelCount : box.fullCount;
      return this.getSelectionList(kind).length >= required;
    }

    return false;
  }

  prefetchStepData(stepName) {
    if (stepName === "travel" || stepName === "full") {
      void this.loadProducts({ showOverlay: false }).then(() => this.refreshStepInventory(stepName));
      return;
    }

    if (stepName === "summary") {
      void Promise.all([
        this.refreshStepInventory("travel"),
        this.refreshStepInventory("full"),
      ]).catch((error) => console.error(error));
    }
  }

  async prepareStepContent(stepName) {
    if (stepName === "design") {
      this.renderDesignGrid();
      return;
    }

    if (stepName === "travel" || stepName === "full") {
      await this.loadProducts({ showOverlay: false });
      await this.refreshStepInventory(stepName);
      this.renderFilterOptions(stepName);
      this.renderProductGrid(stepName);
      this.updateCounter(stepName);
      return;
    }

    if (stepName === "summary") {
      try {
        await this.refreshAllSelectionInventory({ notify: true });
        const pruned = this.lastRemovedSelections || [];
        if (pruned.length) {
          if (this.state.travel.length) this.renderProductGrid("travel");
          if (this.state.full.length) this.renderProductGrid("full");
        }
      } catch (error) {
        console.error(error);
      }
      this.renderSummary();
    }
  }

  async endStepTransition() {
    if (!this.stepTransition) return;

    const minDuration = this.isMobileSidebar() ? 280 : 700;
    const elapsed = Date.now() - (this._stepTransitionStart || 0);
    if (elapsed < minDuration) {
      await this.wait(minDuration - elapsed);
    }

    this.stepTransition.classList.remove("is-visible");
    this.stepTransition.setAttribute("aria-busy", "false");
    await this.wait(this.isMobileSidebar() ? 120 : 200);

    this.stepTransition.setAttribute("hidden", "");
    document.body.classList.remove("byob-step-transition-active");
    this.classList.remove("is-step-transitioning");
    this.isStepTransitioning = false;
  }

  scrollToStart({ behavior = "smooth" } = {}) {
    requestAnimationFrame(() => {
      const target = this.stepper?.hasAttribute("hidden") ? this : this.stepper || this;
      const header =
        document.querySelector(".header-wrapper") ||
        document.querySelector(".site-header") ||
        document.querySelector("header");
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

      window.scrollTo({
        top: Math.max(0, top),
        behavior,
      });
    });
  }

  async goToStep(stepIndex, { skipBeginTransition = false } = {}) {
    const previousStep = this.state.step;
    const stepName = this.steps[stepIndex] || "size";
    const shouldAnimate = stepIndex !== previousStep;

    if (shouldAnimate) {
      this.scrollToStart({ behavior: "instant" });
      if (!skipBeginTransition) {
        await this.beginStepTransition(this.getStepTransitionMessage(stepName));
      }
    }

    this.state.step = stepIndex;

    if (stepIndex !== previousStep) {
      this.prepareStepperAdvance(previousStep);
    }

    if (stepName === "summary") {
      this.returnToSummaryAfterEdit = false;
    }

    try {
      if (stepName !== "size") {
        await this.prepareStepContent(stepName);
      }
    } catch (error) {
      console.error(error);
    }

    Object.entries(this.panels).forEach(([name, panel]) => {
      if (!panel) return;
      panel.toggleAttribute("hidden", name !== stepName);
    });

    if (stepName === "size") {
      this.sidebar?.setAttribute("hidden", "");
      this.nav?.setAttribute("hidden", "");
    } else {
      this.sidebar?.removeAttribute("hidden");
      this.nav?.removeAttribute("hidden");
    }

    try {
      if (shouldAnimate) {
        await this.endStepTransition();
      }
    } finally {
      if (stepName !== "size") {
        this.highlightStepCallout(stepName);
      }
    }

    this.renderSidebar();
    this.updateNavButtons();
    this.renderStepper();
    this.updateLayoutMode();

    if (this.isMobileSidebar() && stepIndex >= 1) {
      this.toggleMobileSidebar(false);
    }

    if (stepIndex >= 1) {
      this.scrollToStart();
    }
  }

  goBack() {
    if (this.state.step <= 1) {
      this.state.step = 0;
      this.state.boxKey = null;
      this.state.boxDesign = null;
      Object.values(this.panels).forEach((panel) => panel?.setAttribute("hidden", ""));
      this.panels.size?.removeAttribute("hidden");
      this.stepper?.setAttribute("hidden", "");
      this.sidebar?.setAttribute("hidden", "");
      this.nav?.setAttribute("hidden", "");
      this.querySelectorAll("[data-byob-box]").forEach((button) => button.classList.remove("is-selected"));
      this.updateLayoutMode();
      this.scrollToStart();
      return;
    }

    this.goToStep(this.state.step - 1);
  }

  async goNext() {
    const stepName = this.currentStepName();
    const box = this.getBoxConfig(this.state.boxKey);

    if (stepName === "design" && !this.state.boxDesign?.variantId) {
      notifier.show(this.config.strings.pickBoxDesign, "error", 3000);
      return;
    }

    if (stepName === "travel" || stepName === "full") {
      const pruned = (await this.refreshStepInventory(stepName)) || [];
      if (pruned.length) {
        this.refreshSelectionUI(stepName);
        notifier.show(this.formatRemovedProductsMessage(pruned), "error", 6000);
      }

      const stockIssues = this.getStockIssues();
      if (stockIssues.length) {
        notifier.show(this.formatStockIssuesMessage(stockIssues), "error", 5000);
        return;
      }
    }

    if (stepName === "travel" && this.state.travel.length < (box.travelCount || 0)) {
      notifier.show(this.config.strings.pickMoreTravel, "error", 3000);
      return;
    }

    if (stepName === "full" && this.state.full.length < (box.fullCount || 0)) {
      notifier.show(this.config.strings.pickMoreFull, "error", 3000);
      return;
    }

    if (this.state.step < this.steps.length - 1) {
      const nextIndex = this.getNextStepIndexAfterComplete(stepName);
      this.goToStep(nextIndex);
    }
  }

  updateNavButtons() {
    const stepName = this.currentStepName();
    const backButton = this.querySelector("[data-byob-back]");
    const continueButton = this.querySelector("[data-byob-continue]");
    const addButtons = this.querySelectorAll("[data-byob-add-to-cart], [data-byob-add-to-cart-inline]");
    const summaryFooter = this.querySelector("[data-byob-summary-footer]");

    backButton?.removeAttribute("hidden");
    continueButton?.toggleAttribute("hidden", stepName === "summary");
    summaryFooter?.toggleAttribute("hidden", stepName !== "summary");
    addButtons.forEach((button) => {
      button.toggleAttribute("hidden", stepName !== "summary");
    });

    if (stepName === "design") {
      const selected = Boolean(this.state.boxDesign?.variantId);
      continueButton?.removeAttribute("hidden");
      continueButton?.toggleAttribute("disabled", !selected);
      continueButton?.setAttribute("aria-disabled", selected ? "false" : "true");
    } else if (stepName === "travel" || stepName === "full") {
      const box = this.getBoxConfig(this.state.boxKey);
      const selected = stepName === "travel" ? this.state.travel.length : this.state.full.length;
      const required = stepName === "travel" ? box.travelCount : box.fullCount;
      continueButton?.removeAttribute("hidden");
      continueButton?.toggleAttribute("disabled", selected < required);
      continueButton?.setAttribute("aria-disabled", selected < required ? "true" : "false");
    }

    if (stepName === "summary") {
      const stockIssues = this.getStockIssues();
      const canAdd = this.isSelectionComplete() && stockIssues.length === 0 && !this.isAddingToCart;
      addButtons.forEach((button) => {
        button.toggleAttribute("disabled", !canAdd);
        button.setAttribute("aria-disabled", !canAdd ? "true" : "false");
      });
    }
  }

  setAddToCartLoading(isLoading) {
    this.isAddingToCart = isLoading;
    const addButtons = this.querySelectorAll("[data-byob-add-to-cart], [data-byob-add-to-cart-inline]");

    addButtons.forEach((button) => {
      button.classList.toggle("loading", isLoading);
      if (isLoading) {
        button.setAttribute("disabled", "");
        button.setAttribute("aria-disabled", "true");
        return;
      }

      button.classList.remove("loading");
    });

    if (!isLoading) {
      this.updateNavButtons();
    }
  }

  getSelectionList(kind) {
    return kind === "travel" ? this.state.travel : this.state.full;
  }

  countVariantInSelection(kind, variantId) {
    const numericId = Number(variantId);
    return this.getSelectionList(kind).filter((item) => item.id === numericId).length;
  }

  getProductVariant(variantId) {
    const numericId = Number(variantId);
    const product = this.products.find((entry) =>
      (entry.variants || []).some((variant) => variant.id === numericId),
    );
    const variant = product?.variants?.find((entry) => entry.id === numericId);
    if (!product || !variant) return null;

    return {
      id: variant.id,
      title: product.title,
      sku: variant.sku || "",
      image: product.images?.[0]?.src || "",
    };
  }

  addProduct(kind, variantId, options = {}) {
    if (!this.canAddVariant(variantId, kind)) {
      const box = this.getBoxConfig(this.state.boxKey);
      const maxCount = kind === "travel" ? box.travelCount : box.fullCount;

      if (this.getSelectionList(kind).length >= maxCount) return;

      notifier.show(this.config.strings.outOfStock, "error", 3500);
      return;
    }

    const item = this.getProductVariant(variantId);
    if (!item) return;

    this.getSelectionList(kind).push(item);
    this.resolveOneRemovedForKind(kind);

    const box = this.getBoxConfig(this.state.boxKey);
    const required = kind === "travel" ? box?.travelCount || 0 : box?.fullCount || 0;
    if (this.getSelectionList(kind).length >= required) {
      this.stockAdjustedByKind[kind] = false;
    }

    this.refreshSelectionUI(kind);
    this.maybeAutoAdvance(kind);
  }

  removeProduct(kind, variantId) {
    const list = this.getSelectionList(kind);
    const numericId = Number(variantId);
    const index = list.findLastIndex((item) => item.id === numericId);
    if (index < 0) return;

    list.splice(index, 1);
    this.abortAutoAdvance();
    this.refreshSelectionUI(kind);
  }

  removeProductAt(kind, index) {
    const list = this.getSelectionList(kind);
    if (index < 0 || index >= list.length) return;

    list.splice(index, 1);
    this.abortAutoAdvance();
    this.refreshSelectionUI(kind);
  }

  refreshSelectionUI(kind) {
    this.updateProductGridSelection(kind);
    this.updateCounter(kind);

    if (this.currentStepName() === "summary") {
      this.renderSummary();
      return;
    }

    this.renderSidebar();
    this.updateNavButtons();
  }

  getProductControlsHtml(variant, kind) {
    const quantity = this.countVariantInSelection(kind, variant.id);
    const canAdd = this.canAddVariant(variant.id, kind);
    const remaining = this.getRemainingVariantQuantity(variant.id);
    const isUnavailable = remaining <= 0 && quantity === 0;

    if (isUnavailable) {
      return `<p class="byob-builder__product-unavailable mb-0">${this.escapeHtml(this.config.strings.productOutOfStock)}</p>`;
    }

    if (quantity > 0) {
      return this.renderQuantityStepper({
        variantId: variant.id,
        kind,
        quantity,
        canAdd,
      });
    }

    return `
      <button
        type="button"
        class="btn btn-primary w-full byob-builder__add-product-btn"
        data-byob-add-product="${variant.id}"
        aria-label="${this.escapeHtml(this.config.strings.addProduct)}"
        ${canAdd ? "" : "disabled"}
      >
        ${this.escapeHtml(this.config.strings.addProduct)}
      </button>
    `;
  }

  getSelectedBadgeHtml() {
    return `
      <span class="byob-builder__product-badge">
        <svg class="byob-builder__product-badge-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${this.escapeHtml(this.config.strings.selected)}
      </span>
    `;
  }

  updateProductCardElement(card, product, kind) {
    const variant = this.getFirstAvailableVariant(product);
    if (!variant?.id || !card) return;

    const quantity = this.countVariantInSelection(kind, variant.id);
    const remaining = this.getRemainingVariantQuantity(variant.id);
    const isUnavailable = remaining <= 0 && quantity === 0;
    const stockHint = this.getStockHint(variant);

    card.classList.toggle("is-selected", quantity > 0);
    card.classList.toggle("is-unavailable", isUnavailable);

    const media = card.querySelector(".byob-builder__product-media");
    const existingBadge = media?.querySelector(".byob-builder__product-badge");
    if (quantity > 0 && !existingBadge) {
      media?.insertAdjacentHTML("beforeend", this.getSelectedBadgeHtml());
    } else if (quantity === 0 && existingBadge) {
      existingBadge.remove();
    }

    const body = card.querySelector(".byob-builder__product-body");
    const controls = body?.querySelector("[data-byob-product-controls]");
    if (controls) {
      controls.innerHTML = this.getProductControlsHtml(variant, kind);
    }

    const stockEl = body?.querySelector("[data-byob-stock-hint]");
    if (stockHint) {
      if (stockEl) {
        stockEl.textContent = stockHint;
      } else {
        const title = body?.querySelector(".byob-builder__product-title");
        title?.insertAdjacentHTML(
          "afterend",
          `<p class="byob-builder__stock-hint mb-0" data-byob-stock-hint>${this.escapeHtml(stockHint)}</p>`,
        );
      }
    } else if (stockEl) {
      stockEl.remove();
    }
  }

  updateProductGridSelection(kind) {
    const grid = this.querySelector(`[data-byob-grid="${kind}"]`);
    const empty = this.querySelector(`[data-byob-empty="${kind}"]`);
    if (!grid) return;

    const products = this.getFilteredProducts(kind);
    if (!products.length) {
      this.renderProductGrid(kind);
      return;
    }

    const variantIds = products
      .map((product) => String(this.getFirstAvailableVariant(product)?.id || ""))
      .filter(Boolean);
    const existingCards = [...grid.querySelectorAll("[data-product-id]")];
    const existingIds = existingCards.map((card) => card.dataset.productId);

    const needsFullRender =
      existingCards.length !== variantIds.length ||
      variantIds.some((id, index) => existingIds[index] !== id);

    if (needsFullRender) {
      this.renderProductGrid(kind);
      return;
    }

    empty?.setAttribute("hidden", "");
    products.forEach((product) => {
      const variant = this.getFirstAvailableVariant(product);
      const card = grid.querySelector(`[data-product-id="${variant?.id}"]`);
      this.updateProductCardElement(card, product, kind);
    });
  }

  maybeAutoAdvance(kind) {
    this.clearAutoAdvanceTimers();

    const box = this.getBoxConfig(this.state.boxKey);
    if (!box || this.currentStepName() !== kind) return;
    if (this.state.step >= this.steps.length - 1) return;

    let message = "";
    let isComplete = false;

    if (kind === "design") {
      isComplete = Boolean(this.state.boxDesign?.variantId);
      const nextStepName = this.steps[this.state.step + 1];
      message =
        nextStepName === "full"
          ? this.config.strings.autoAdvanceDesignFull
          : this.config.strings.autoAdvanceDesign;
    } else if (kind === "travel" || kind === "full") {
      const required = kind === "travel" ? box.travelCount : box.fullCount;
      if (!required || this.getSelectionList(kind).length < required) return;
      isComplete = true;
      message =
        kind === "travel" ? this.config.strings.autoAdvanceTravel : this.config.strings.autoAdvanceFull;
    } else {
      return;
    }

    if (!isComplete || !message) return;

    const nextStepIndex = this.getNextStepIndexAfterComplete(kind);
    const nextStepName = this.steps[nextStepIndex];
    const advanceDelay = 700;

    this._autoAdvanceTimeout = window.setTimeout(() => {
      this._autoAdvanceTimeout = null;
      if (!this.isAutoAdvanceStillValid(kind, box)) return;

      this._autoAdvanceActive = true;
      this.prefetchStepData(nextStepName);

      void (async () => {
        await this.beginStepTransition(this.getStepTransitionMessage(nextStepName));

        this._autoAdvanceStepTimeout = window.setTimeout(() => {
          this._autoAdvanceStepTimeout = null;
          if (!this.isAutoAdvanceStillValid(kind, box)) {
            this.abortAutoAdvance();
            return;
          }

          this._autoAdvanceActive = false;
          void this.goToStep(nextStepIndex, { skipBeginTransition: true });
        }, advanceDelay);
      })();
    }, 120);
  }

  highlightStepCallout(stepName) {
    const callout = this.panels[stepName]?.querySelector(".byob-builder__step-callout");
    if (!callout) return;

    callout.classList.remove("is-entering");
  }

  scrollSidebarToFocus() {
    const itemsEl = this.querySelector("[data-byob-sidebar-items]");
    if (!itemsEl) return;

    requestAnimationFrame(() => {
      const firstEmpty = itemsEl.querySelector(".byob-builder__sidebar-slot");
      const target = firstEmpty || itemsEl.lastElementChild;
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  renderSidebar() {
    const subtitle = this.querySelector("[data-byob-sidebar-subtitle]");
    const progressWrap = this.querySelector("[data-byob-sidebar-progress]");
    const progressFill = this.querySelector("[data-byob-progress-fill]");
    const status = this.querySelector("[data-byob-sidebar-status]");
    const mobileSummary = this.querySelector("[data-byob-sidebar-mobile-summary]");
    const itemsEl = this.querySelector("[data-byob-sidebar-items]");
    const divider = this.querySelector("[data-byob-sidebar-divider]");

    if (!this.sidebar || this.state.step === 0 || !this.state.boxKey) {
      this.sidebar?.setAttribute("hidden", "");
      return;
    }

    this.sidebar.removeAttribute("hidden");
    const box = this.getBoxConfig(this.state.boxKey);
    if (subtitle && box) subtitle.textContent = box.title;

    const travelRequired = box?.travelCount || 0;
    const fullRequired = box?.fullCount || 0;
    const totalRequired = travelRequired + fullRequired;
    const totalSelected = this.state.travel.length + this.state.full.length;

    if (progressWrap && totalRequired > 0) {
      progressWrap.removeAttribute("hidden");
      const progress = Math.min(100, (totalSelected / totalRequired) * 100);
      if (progressFill) progressFill.style.width = `${progress}%`;

      const parts = [];
      if (travelRequired) {
        parts.push(this.formatSizeProgress("travel", this.state.travel.length, travelRequired));
      }
      if (fullRequired) {
        parts.push(this.formatSizeProgress("full", this.state.full.length, fullRequired));
      }
      if (status) status.textContent = parts.join(" · ");

      if (mobileSummary) {
        const lines = [];
        if (travelRequired) {
          lines.push({
            text: this.formatMobileSelectionLine("travel", this.state.travel.length, travelRequired),
            complete: this.state.travel.length >= travelRequired,
          });
        }
        if (fullRequired) {
          lines.push({
            text: this.formatMobileSelectionLine("full", this.state.full.length, fullRequired),
            complete: this.state.full.length >= fullRequired,
          });
        }

        mobileSummary.innerHTML = lines
          .map(
            (line) =>
              `<span class="byob-builder__sidebar-mobile-line${line.complete ? " is-complete" : ""}">${this.escapeHtml(line.text)}</span>`,
          )
          .join("");
      }
    } else {
      progressWrap?.setAttribute("hidden", "");
      if (mobileSummary) mobileSummary.innerHTML = "";
    }

    this.updateMobileDesignThumb();
    this.updateMobileSidebarHint();

    divider?.toggleAttribute("hidden", false);

    if (!itemsEl) return;

    const groups = [];
    if (this.hasBoxDesignStep()) {
      groups.push(this.renderSidebarDesignGroup());
    }
    if (travelRequired > 0) {
      groups.push(
        this.renderSidebarGroup(this.getSizeLabel("travel", "products"), "travel", travelRequired),
      );
    }
    if (fullRequired > 0) {
      groups.push(
        this.renderSidebarGroup(this.getSizeLabel("full", "products"), "full", fullRequired),
      );
    }
    itemsEl.innerHTML = groups.join("");

    itemsEl.querySelectorAll("[data-byob-sidebar-remove]").forEach((button) => {
      button.addEventListener("click", () => {
        this.removeProduct(button.dataset.byobSidebarKind, button.dataset.byobSidebarRemove);
      });
    });
    itemsEl.querySelectorAll("[data-byob-sidebar-add]").forEach((button) => {
      button.addEventListener("click", (event) => {
        this.addProduct(button.dataset.byobSidebarKind, button.dataset.byobSidebarAdd);
      });
    });
    itemsEl.querySelectorAll("[data-byob-goto-step]").forEach((button) => {
      button.addEventListener("click", () => {
        this.goToPickStep(button.dataset.byobGotoStep);
        this.toggleMobileSidebar(false);
      });
    });

    this.scrollSidebarToFocus();
  }

  renderSidebarChangeButton(stepName) {
    return `
      <button
        type="button"
        class="byob-builder__sidebar-change-btn"
        data-byob-goto-step="${stepName}"
      >
        ${this.escapeHtml(this.config.strings.change)}
      </button>
    `;
  }

  renderSidebarDesignGroup() {
    const label = this.config.strings.stepDesignBadge;
    const emptyLabel = this.config.strings.sidebarPickDesign;
    const design = this.state.boxDesign;

    const content = design
      ? `
        <div class="byob-builder__sidebar-item flex gap-sp-4 align-center">
          ${
            design.image
              ? `<figure class="byob-builder__sidebar-media media rounded-10 overflow-hidden"><img src="${this.escapeHtml(design.image)}" alt="" width="80" height="100" loading="lazy"></figure>`
              : `<div class="byob-builder__sidebar-media byob-builder__sidebar-media--empty rounded-10 bg-secondary"></div>`
          }
          <div class="grow-1 min-w-0">
            <p class="byob-builder__product-title">${this.escapeHtml(design.title)}</p>
          </div>
          <div class="byob-builder__sidebar-qty flex-shrink-0">
            ${this.renderSidebarChangeButton("design")}
          </div>
        </div>
      `
      : this.renderSidebarSlot(emptyLabel, "design", this.needsReplacementPlaceholder("design", 1));

    return `
      <div class="byob-builder__sidebar-group">
        <p class="byob-builder__sidebar-group-title">${this.escapeHtml(label)}</p>
        <div class="flex column gap-sp-4">
          ${content}
        </div>
      </div>
    `;
  }

  renderSidebarGroup(label, kind, required) {
    const list = this.getSelectionList(kind);
    const aggregated = this.aggregateSelectionItems(list);
    const emptyLabel =
      kind === "travel"
        ? this.formatPickSizeProductLabel("travel")
        : this.formatPickSizeProductLabel("full");

    const removedItems = this.getRemovedForKind(kind);
    const items = aggregated.map((item) => this.renderSidebarItem(item, kind)).join("");
    const emptySlots = Math.max(0, required - list.length - removedItems.length);
    const needsReplacement = this.needsReplacementPlaceholder(kind, emptySlots);
    const placeholders = Array.from({ length: emptySlots }, () =>
      this.renderSidebarSlot(emptyLabel, kind, needsReplacement),
    ).join("");

    return `
      <div class="byob-builder__sidebar-group">
        <p class="byob-builder__sidebar-group-title">${this.escapeHtml(label)}</p>
        <div class="flex column gap-sp-4">
          ${items}
          ${placeholders}
        </div>
      </div>
    `;
  }

  renderStepperIcon(type) {
    if (type === "minus") {
      return `<svg class="byob-builder__qty-stepper-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    }

    return `<svg class="byob-builder__qty-stepper-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
  }

  renderQuantityStepper({ variantId, kind, quantity, canAdd, compact = false }) {
    const classes = ["byob-builder__qty-stepper"];
    if (compact) classes.push("byob-builder__qty-stepper--compact");

    if (compact) {
      return `
        <div class="${classes.join(" ")}" role="group" aria-label="${this.escapeHtml(this.config.strings.add)}">
          <button
            type="button"
            class="byob-builder__qty-stepper-btn"
            data-byob-sidebar-remove="${variantId}"
            data-byob-sidebar-kind="${kind}"
            aria-label="${this.escapeHtml(this.config.strings.remove)}"
          >${this.renderStepperIcon("minus")}</button>
          <span class="byob-builder__qty-stepper-value" aria-live="polite">${quantity}</span>
          <button
            type="button"
            class="byob-builder__qty-stepper-btn"
            data-byob-sidebar-add="${variantId}"
            data-byob-sidebar-kind="${kind}"
            aria-label="${this.escapeHtml(this.config.strings.add)}"
            ${canAdd ? "" : "disabled"}
          >${this.renderStepperIcon("plus")}</button>
        </div>
      `;
    }

    return `
      <div class="${classes.join(" ")}" role="group" aria-label="${this.escapeHtml(this.config.strings.add)}">
        <button
          type="button"
          class="byob-builder__qty-stepper-btn"
          data-byob-remove="${variantId}"
          aria-label="${this.escapeHtml(this.config.strings.remove)}"
        >${this.renderStepperIcon("minus")}</button>
        <span class="byob-builder__qty-stepper-value" aria-live="polite">${quantity}</span>
        <button
          type="button"
          class="byob-builder__qty-stepper-btn"
          data-byob-add-product="${variantId}"
          aria-label="${this.escapeHtml(this.config.strings.add)}"
          ${canAdd ? "" : "disabled"}
        >${this.renderStepperIcon("plus")}</button>
      </div>
    `;
  }

  renderSidebarItem(item, kind) {
    const canAdd = this.canAddVariant(item.id, kind);

    return `
      <div class="byob-builder__sidebar-item flex gap-sp-4 align-center">
        ${
          item.image
            ? `<figure class="byob-builder__sidebar-media media rounded-10 overflow-hidden"><img src="${item.image}" alt="" width="80" height="100" loading="lazy"></figure>`
            : `<div class="byob-builder__sidebar-media byob-builder__sidebar-media--empty rounded-10 bg-secondary"></div>`
        }
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title">${this.escapeHtml(item.title)}</p>
        </div>
        <div class="byob-builder__sidebar-qty flex-shrink-0">
          ${this.renderQuantityStepper({
            variantId: item.id,
            kind,
            quantity: item.quantity,
            canAdd,
            compact: true,
          })}
        </div>
      </div>
    `;
  }

  renderSidebarSlot(label, kind, needsReplacement = false) {
    const replacementClass = needsReplacement ? " byob-builder__sidebar-slot--needs-replacement" : "";

    return `
      <button
        type="button"
        class="byob-builder__sidebar-slot${replacementClass} btn-reset w-full text-left"
        data-byob-goto-step="${kind}"
        aria-label="${this.escapeHtml(label)}"
      >
        <span class="flex gap-sp-4 align-center">
          <span class="byob-builder__sidebar-media byob-builder__sidebar-media--empty rounded-10" aria-hidden="true"></span>
          <span class="mb-0 heading-color">${this.escapeHtml(label)}</span>
        </span>
      </button>
    `;
  }

  getFilteredProducts(kind) {
    const filters = this.getGridFilters(kind);
    const search = filters.search.trim().toLowerCase();

    let products = this.getEligibleProducts(kind);

    if (filters.brand) {
      products = products.filter((product) => product.vendor === filters.brand);
    }

    if (filters.productType) {
      products = products.filter(
        (product) => this.getProductType(product) === filters.productType,
      );
    }

    if (search) {
      products = products.filter((product) => {
        const haystack = `${product.title} ${product.vendor} ${this.getProductType(product)} ${(product.variants || [])
          .map((variant) => variant.sku)
          .join(" ")}`.toLowerCase();
        return haystack.includes(search);
      });
    }

    return this.sortProducts(products, filters.sort);
  }

  renderProductGrid(kind) {
    const grid = this.querySelector(`[data-byob-grid="${kind}"]`);
    const empty = this.querySelector(`[data-byob-empty="${kind}"]`);
    if (!grid) return;

    const products = this.getFilteredProducts(kind);
    const box = this.getBoxConfig(this.state.boxKey);
    const maxCount = kind === "travel" ? box.travelCount : box.fullCount;
    const selectedCount = this.getSelectionList(kind).length;
    const atLimit = selectedCount >= maxCount;

    if (!products.length) {
      grid.innerHTML = "";
      empty?.removeAttribute("hidden");
      if (empty) {
        const filters = this.getGridFilters(kind);
        const hasActiveFilters =
          filters.brand || filters.productType || filters.search.trim();
        const message = hasActiveFilters
          ? this.config.strings.noProductsFilters
          : this.config.strings.noProducts;
        const tag = kind === "travel" ? this.config.travelTag : this.config.fullTag;
        empty.innerHTML = hasActiveFilters
          ? `<p>${this.escapeHtml(message)}</p>`
          : `<p>${this.escapeHtml(message)}</p><p class="fs-small opacity-70">${this.escapeHtml(this.config.strings.noProductsTagHint.replace("[tag]", tag))}</p>`;
      }
      return;
    }

    empty?.setAttribute("hidden", "");
    grid.innerHTML = products
      .map((product) => this.renderProductCard(product, kind, atLimit))
      .join("");
  }

  getFirstAvailableVariant(product) {
    const variants = product.variants || [];
    return (
      variants.find((variant) => this.isVariantSelectable(variant)) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  }

  renderProductBadges(product, kind) {
    const valueLabel = this.getProductValueLabel(product);
    const sizeLabel =
      kind === "travel"
        ? this.config.strings.productBadgeTravel
        : this.config.strings.productBadgeFull;

    return `
      <div class="byob-builder__product-badges${valueLabel ? "" : " byob-builder__product-badges--size-only"}">
        ${
          valueLabel
            ? `<span class="byob-builder__product-badge-pill byob-builder__product-badge-pill--value">${this.escapeHtml(valueLabel)}</span>`
            : ""
        }
        <span class="byob-builder__product-badge-pill byob-builder__product-badge-pill--size">${this.escapeHtml(sizeLabel)}</span>
      </div>
    `;
  }

  renderProductCard(product, kind, atLimit) {
    const variant = this.getFirstAvailableVariant(product);
    if (!variant?.id) return "";

    const quantity = this.countVariantInSelection(kind, variant.id);
    const remaining = this.getRemainingVariantQuantity(variant.id);
    const isUnavailable = remaining <= 0 && quantity === 0;
    const stockHint = this.getStockHint(variant);
    const image = product.images?.[0];
    const imageHtml = image
      ? `<img src="${image.src}" alt="${this.escapeHtml(product.title)}" loading="eager" decoding="async" width="400" height="500">`
      : "";

    return `
      <article class="byob-builder__product-card${quantity > 0 ? " is-selected" : ""}${isUnavailable ? " is-unavailable" : ""}" data-product-id="${variant.id}">
        <div class="byob-builder__product-media">
          ${this.renderProductBadges(product, kind)}
          ${imageHtml}
          ${quantity > 0 ? this.getSelectedBadgeHtml() : ""}
        </div>
        <div class="byob-builder__product-body">
          ${product.vendor ? `<p class="byob-builder__product-vendor">${this.escapeHtml(product.vendor)}</p>` : ""}
          <p class="byob-builder__product-title">${this.escapeHtml(product.title)}</p>
          ${stockHint ? `<p class="byob-builder__stock-hint mb-0" data-byob-stock-hint>${this.escapeHtml(stockHint)}</p>` : ""}
          <div data-byob-product-controls>${this.getProductControlsHtml(variant, kind)}</div>
        </div>
      </article>
    `;
  }

  aggregateSelectionItems(items) {
    const grouped = new Map();

    items.forEach((item) => {
      const key = String(item.id);
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += 1;
      } else {
        grouped.set(key, { ...item, quantity: 1 });
      }
    });

    return Array.from(grouped.values());
  }

  updateCounter(kind) {
    const box = this.getBoxConfig(this.state.boxKey);
    const selected = kind === "travel" ? this.state.travel.length : this.state.full.length;
    const required = kind === "travel" ? box.travelCount : box.fullCount;
    const counter = this.querySelector(`[data-byob-counter="${kind}"]`);
    if (counter) counter.textContent = `${selected} / ${required}`;
  }

  getSelectionTotalValueCents() {
    let total = 0;

    this.aggregateSelectionItems([...this.state.travel, ...this.state.full]).forEach((item) => {
      const record = this.getVariantRecord(item.id);
      if (!record?.product) return;

      total += this.getProductValueAmount(record.product) * item.quantity;
    });

    return total;
  }

  formatSelectionTotalValueAmount() {
    const cents = this.getSelectionTotalValueCents();
    const moneyFormat = window.themeGlobalVariables?.settings?.money_format;
    if (cents <= 0 || !moneyFormat) return "";

    return NextSkyTheme.formatMoney(cents, moneyFormat);
  }

  needsReplacementPlaceholder(kind, emptySlots = 0) {
    if (kind === "design") {
      return this.hasBoxDesignStep() && !this.state.boxDesign?.variantId;
    }

    return emptySlots > 0;
  }

  formatBoxPrice(box) {
    const cents = Number(box?.priceCents);
    const moneyFormat = window.themeGlobalVariables?.settings?.money_format;
    if (!Number.isFinite(cents) || cents <= 0 || !moneyFormat) return "";

    const formatted = NextSkyTheme.formatMoney(cents, moneyFormat);
    return String(this.config.strings.summaryBoxPrice || "Price: {{ price }}").replace(
      /\{\{\s*price\s*\}\}/g,
      formatted,
    );
  }

  renderSummaryPlaceholderCard(kind, { needsReplacement = false } = {}) {
    const label =
      kind === "travel"
        ? this.formatPickSizeProductLabel("travel")
        : kind === "full"
          ? this.formatPickSizeProductLabel("full")
          : this.config.strings.sidebarPickDesign;
    const replacementClass = needsReplacement ? " byob-builder__summary-card--needs-replacement" : "";

    return `
      <button
        type="button"
        class="byob-builder__summary-card byob-builder__summary-card--placeholder${replacementClass} border rounded flex gap-sp-4 align-center p-sp-3 btn-reset w-full text-left"
        data-byob-goto-step="${kind}"
        aria-label="${this.escapeHtml(label)}"
      >
        <div class="byob-builder__summary-card-img byob-builder__summary-card-img--empty rounded-10 flex-shrink-0" aria-hidden="true"></div>
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title mb-0">${this.escapeHtml(label)}</p>
        </div>
      </button>
    `;
  }

  renderSummarySoldOutCard(item, kind) {
    const soldOutMessage = String(
      this.config.strings.summarySlotSoldOut ||
        this.config.strings.productSoldOutSingle ||
        "{{ product }} has unfortunately sold out.",
    ).replace(/\{\{\s*product\s*\}\}/g, item.title || this.config.strings.productOutOfStock || "Product");
    const pickReplacement = this.config.strings.summaryPickReplacement || "Pick a replacement";

    return `
      <button
        type="button"
        class="byob-builder__summary-card byob-builder__summary-card--sold-out is-stock-flagged border rounded flex gap-sp-4 align-center p-sp-3 btn-reset w-full text-left"
        data-byob-goto-step="${kind}"
        aria-label="${this.escapeHtml(`${item.title || "Product"}. ${soldOutMessage}`)}"
      >
        ${
          item.image
            ? `<img src="${this.escapeHtml(item.image)}" alt="" class="byob-builder__summary-card-img rounded-10 flex-shrink-0 opacity-50" width="72" height="90" loading="lazy">`
            : `<div class="byob-builder__summary-card-img byob-builder__summary-card-img--empty byob-builder__summary-card-img--sold-out rounded-10 flex-shrink-0" aria-hidden="true"></div>`
        }
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title mb-sp-1">${this.escapeHtml(item.title || "")}</p>
          <p class="byob-builder__summary-card-alert mb-sp-1">${this.escapeHtml(soldOutMessage)}</p>
          <p class="fs-small mb-0">${this.escapeHtml(pickReplacement)}</p>
        </div>
      </button>
    `;
  }

  renderSummarySectionItems(kind, required, items) {
    const list = this.getSelectionList(kind);
    const removedItems = this.getRemovedForKind(kind);
    const productCards = items.map((item) => this.renderSummaryCard(item)).join("");
    const soldOutCards = removedItems.map((item) => this.renderSummarySoldOutCard(item, kind)).join("");
    const emptySlots = Math.max(0, required - list.length - removedItems.length);
    const needsReplacement = this.needsReplacementPlaceholder(kind, emptySlots);
    const placeholders = Array.from({ length: emptySlots }, () =>
      this.renderSummaryPlaceholderCard(kind, { needsReplacement }),
    ).join("");

    return `${productCards}${soldOutCards}${placeholders}`;
  }

  renderSummarySection({ label, kind, editLabel, items, singleColumn = false, hideEdit = false }) {
    const gridClass = singleColumn
      ? "byob-builder__summary-grid byob-builder__summary-grid--single"
      : "byob-builder__summary-grid";
    const editButton = hideEdit
      ? ""
      : `
          <button type="button" class="byob-builder__text-link" data-byob-goto-step="${kind}">
            ${this.escapeHtml(editLabel)}
          </button>
        `;
    const sectionNotice = this.getSummarySectionNotice(kind);
    const noticeHtml = sectionNotice
      ? `<p class="byob-builder__summary-section-notice mb-sp-4" role="status">${this.escapeHtml(sectionNotice)}</p>`
      : "";

    return `
      <section class="byob-builder__summary-section mb-sp-6">
        <div class="byob-builder__summary-section-header flex flex-wrap gap-sp-3 space-between align-center mb-sp-4">
          <h3 class="byob-builder__summary-section-title mb-0">${this.escapeHtml(label)}</h3>
          ${editButton}
        </div>
        ${noticeHtml}
        <div class="${gridClass}">
          ${items}
        </div>
      </section>
    `;
  }

  formatBoxDescription(box) {
    const parts = [];

    if (box.travelCount) {
      parts.push(`${box.travelCount} ${this.getSizeLabel("travel", "products")}`);
    }

    if (box.fullCount) {
      parts.push(`${box.fullCount} ${this.getSizeLabel("full", "products")}`);
    }

    return parts.join(" + ");
  }

  renderSummary() {
    const box = this.getBoxConfig(this.state.boxKey);
    const container = this.querySelector("[data-byob-summary-content]");
    if (!box || !container) {
      this.renderSidebar();
      return;
    }

    const groups = [];
    if (box.travelCount > 0) {
      groups.push({
        label: this.config.strings.summaryTravelHeading,
        kind: "travel",
        required: box.travelCount,
        items: this.aggregateSelectionItems(this.state.travel),
      });
    }
    if (box.fullCount > 0) {
      groups.push({
        label: this.config.strings.summaryFullHeading,
        kind: "full",
        required: box.fullCount,
        items: this.aggregateSelectionItems(this.state.full),
      });
    }

    const boxDescription =
      this.config.boxDescriptions?.[this.state.boxKey] || this.formatBoxDescription(box);
    const boxPriceLabel = this.formatBoxPrice(box);
    const totalValueAmount = this.formatSelectionTotalValueAmount();
    const boxPriceHtml = boxPriceLabel
      ? `<p class="byob-builder__summary-hero-price mb-0">${this.escapeHtml(boxPriceLabel)}</p>`
      : "";
    const totalValueBadge = totalValueAmount
      ? `<p class="byob-builder__summary-value-row mb-sp-2">
          <span class="byob-builder__summary-value-label">${this.escapeHtml(this.config.strings.summaryTotalValueLabel || "Total value in your box:")}</span>
          <span class="byob-builder__summary-value-pill">${this.escapeHtml(totalValueAmount)}</span>
        </p>`
      : "";

    const sections = [];

    if (this.hasBoxDesignStep()) {
      sections.push(
        this.renderSummarySection({
          label: this.config.strings.summaryDesignHeading,
          kind: "design",
          editLabel: this.config.strings.summaryEditDesign,
          items: this.state.boxDesign
            ? this.renderSummaryDesignCard(this.state.boxDesign)
            : this.renderSummaryPlaceholderCard("design", {
                needsReplacement: this.needsReplacementPlaceholder("design"),
              }),
          singleColumn: true,
        }),
      );
    }

    groups.forEach((group) => {
      sections.push(
        this.renderSummarySection({
          label: group.label,
          kind: group.kind,
          editLabel:
            group.kind === "travel"
              ? this.config.strings.summaryEditTravel
              : this.config.strings.summaryEditFull,
          items: this.renderSummarySectionItems(group.kind, group.required, group.items),
        }),
      );
    });

    const freeGift = this.getFreeGift();
    if (freeGift) {
      sections.push(
        this.renderSummarySection({
          label: this.config.strings.summaryGiftHeading,
          kind: "gift",
          items: this.renderSummaryGiftCard(freeGift),
          singleColumn: true,
          hideEdit: true,
        }),
      );
    }

    const stockIssues = this.getStockIssues();
    const removed = [...this.getRemovedForKind("travel"), ...this.getRemovedForKind("full")];
    const alertMessage = this.getSummaryStockAlertMessage({ stockIssues, removed });
    const stockWarning = alertMessage
      ? `
        <div class="byob-builder__summary-stock-alert border rounded p-sp-4 mb-sp-6" role="alert">
          <p class="byob-builder__summary-stock-alert-title mb-sp-2">${this.escapeHtml(this.config.strings.summaryStockWarningTitle || "Some products are no longer available")}</p>
          <p class="mb-0">${this.escapeHtml(alertMessage)}</p>
        </div>
      `
      : "";

    container.innerHTML = `
      ${stockWarning}
      <div class="byob-builder__summary-hero border rounded p-sp-5 mb-sp-6">
        <p class="byob-builder__summary-hero-label mb-sp-2">${this.escapeHtml(this.config.strings.yourBox)}</p>
        <h3 class="byob-builder__summary-hero-title mb-sp-2">${this.escapeHtml(box.title)}</h3>
        <p class="byob-builder__summary-hero-desc mb-sp-3">${this.escapeHtml(boxDescription)}</p>
        ${totalValueBadge}
        ${boxPriceHtml}
      </div>
      ${sections.join("")}
    `;

    container.querySelectorAll("[data-byob-goto-step]").forEach((button) => {
      button.addEventListener("click", () => {
        this.goToPickStep(button.dataset.byobGotoStep);
      });
    });

    this.renderSidebar();
    this.updateNavButtons();
  }

  renderSummaryDesignCard(design) {
    return `
      <div class="byob-builder__summary-card border rounded flex gap-sp-4 align-center p-sp-3">
        ${
          design.image
            ? `<img src="${this.escapeHtml(design.image)}" alt="" class="byob-builder__summary-card-img rounded-10 flex-shrink-0" width="72" height="90" loading="lazy">`
            : `<div class="byob-builder__summary-card-img byob-builder__summary-card-img--empty rounded-10 bg-secondary flex-shrink-0" aria-hidden="true"></div>`
        }
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title">${this.escapeHtml(design.title)}</p>
        </div>
      </div>
    `;
  }

  renderSummaryGiftCard(gift) {
    const badge = this.config.strings.summaryGiftBadge || "Free gift";

    return `
      <div class="byob-builder__summary-card byob-builder__summary-card--gift border rounded flex gap-sp-4 align-center p-sp-3">
        ${
          gift.image
            ? `<img src="${this.escapeHtml(gift.image)}" alt="" class="byob-builder__summary-card-img rounded-10 flex-shrink-0" width="72" height="90" loading="lazy">`
            : `<div class="byob-builder__summary-card-img byob-builder__summary-card-img--empty rounded-10 bg-secondary flex-shrink-0" aria-hidden="true"></div>`
        }
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title">${this.escapeHtml(gift.title)}</p>
          <p class="fs-small mb-0 mt-sp-1 byob-builder__summary-gift-badge">${this.escapeHtml(badge)}</p>
        </div>
      </div>
    `;
  }

  renderSummaryCard(item) {
    const record = this.getVariantRecord(item.id);
    const valueLabel = record?.product ? this.getProductValueLabel(record.product) : "";

    return `
      <div class="byob-builder__summary-card border rounded flex gap-sp-4 align-center p-sp-3">
        ${
          item.image
            ? `<img src="${item.image}" alt="" class="byob-builder__summary-card-img rounded-10 flex-shrink-0" width="72" height="90" loading="lazy">`
            : `<div class="byob-builder__summary-card-img byob-builder__summary-card-img--empty rounded-10 bg-secondary flex-shrink-0" aria-hidden="true"></div>`
        }
        <div class="grow-1 min-w-0">
          <p class="byob-builder__product-title">${this.escapeHtml(item.title)}</p>
          ${valueLabel ? `<p class="fs-small opacity-60 mb-0 mt-sp-1">${this.escapeHtml(valueLabel)}</p>` : ""}
        </div>
        <span class="byob-builder__summary-card-qty flex-shrink-0">×${item.quantity}</span>
      </div>
    `;
  }

  buildLineItemProperties(bundleId, { includeGiftOnShell = false, freeGift = null } = {}) {
    const properties = {};
    const variantIds = [];

    [...this.state.travel, ...this.state.full].forEach((item) => {
      variantIds.push(item.id);
    });

    this.aggregateSelectionItems([...this.state.travel, ...this.state.full]).forEach((item) => {
      const label = item.sku ? `${item.title} (${item.sku})` : item.title;
      properties[label] = `x${item.quantity}`;
    });

    if (this.state.boxDesign?.variantId) {
      const design = this.state.boxDesign;
      const label = design.sku ? `${design.title} (${design.sku})` : design.title;
      properties[label] = "x1";
      properties._byob_design_variant_id = String(design.variantId);
      variantIds.push(design.variantId);
    }

    if (variantIds.length) {
      properties._byob_variant_ids = variantIds.map((id) => String(id)).join(",");
    }

    if (this.state.boxKey) {
      properties._byob_box_key = this.state.boxKey;
    }

    if (this.state.travel.length) {
      properties._byob_travel_ids = this.state.travel.map((item) => String(item.id)).join(",");
    }

    if (this.state.full.length) {
      properties._byob_full_ids = this.state.full.map((item) => String(item.id)).join(",");
    }

    if (includeGiftOnShell && freeGift?.variantId) {
      Object.assign(properties, this.buildGiftShellProperties(freeGift));
    }

    properties._byob_role = "shell";
    if (bundleId) {
      properties._byob_bundle_id = bundleId;
    }

    return properties;
  }

  async addToCart() {
    const box = this.getBoxConfig(this.state.boxKey);
    const activeButton =
      this.querySelector("[data-byob-add-to-cart-inline]:not([hidden])") ||
      this.querySelector("[data-byob-add-to-cart]:not([hidden])");
    if (!box?.variantId || this.isAddingToCart || activeButton?.getAttribute("aria-disabled") === "true") {
      return;
    }

    let keepOverlay = false;
    this.setAddToCartLoading(true);
    await this.beginStepTransition(this.config.strings.addingToCart || this.config.strings.add_to_cart);

    try {
      const pruned = await this.refreshAllSelectionInventory({ notify: true });
      if (pruned.length) {
        this.renderSummary();
        if (this.state.travel.length) this.renderProductGrid("travel");
        if (this.state.full.length) this.renderProductGrid("full");
        this.renderSidebar();
        this.updateNavButtons();
      }

      const stockIssues = this.getStockIssues();
      if (stockIssues.length) {
        notifier.show(this.formatStockIssuesMessage(stockIssues), "error", 6000);
        return;
      }

      const bundleId = createBundleId();
      const freeGift = this.getFreeGift();
      const shellProperties = this.buildLineItemProperties(bundleId);
      const sections = this.cart
        ? this.cart.getSectionsToRender().map((section) => section.section || section.id)
        : null;
      const shellItem = {
        id: box.variantId,
        quantity: 1,
        properties: shellProperties,
      };

      let data = await this.postCartAdd({ items: [shellItem], sections });

      if (data?.status) {
        const detail = [data.description, data.message].filter(Boolean).join(" ");
        const shellMessage = [
          this.config.strings.shellCartError || this.config.strings.cartError,
          detail,
        ]
          .filter(Boolean)
          .join(" ");
        notifier.show(shellMessage, "error", 6000);
        return;
      }

      let giftNotice = null;

      if (freeGift?.variantId) {
        try {
          await this.waitForCartVariant(box.variantId, bundleId);
          await this.syncLocksmithCart();
          await setByobCartAttribute(true);

          const giftData = await this.postCartAdd({
            items: [
              {
                id: freeGift.variantId,
                quantity: 1,
                properties: {
                  _byob_role: "gift",
                  _byob_bundle_id: bundleId,
                },
              },
            ],
            sections,
          });

          if (giftData?.status) {
            const shellLineKey =
              this.getShellLineKey(data, box.variantId, bundleId) ||
              (await this.resolveShellLineKey(box.variantId, bundleId));
            await this.attachGiftPropertiesToShell(shellLineKey, shellProperties, freeGift);
            giftNotice =
              this.config.strings.giftCartFallback || this.config.strings.giftCartError;
          } else {
            data = giftData;
          }
        } catch (giftError) {
          console.error(giftError);
          giftNotice =
            this.config.strings.giftCartFallback || this.config.strings.giftCartError;
        }
      }

      if (giftNotice) {
        notifier.show(giftNotice, "success", 5000);
      }

      try {
        const cartSnapshot = await fetch(`${getCartRoot().replace(/\/?$/, "/")}cart.js`).then((response) =>
          response.json(),
        );
        await syncByobValidationCartAttribute(
          cartSnapshot.items,
          buildByobNameMapFromState(this.state),
          buildByobImageMapFromState(this.state),
        );
      } catch (attributeError) {
        console.error(attributeError);
      }

      if (!this.cart || document.body.classList.contains("template-cart")) {
        keepOverlay = true;
        window.location = window.routes.cart_url;
        return;
      }

      this.cart.renderContents(data);
      eventModal(this.cart, "open", false, "delay");
      this.resetBuilder();
    } catch (error) {
      console.error(error);
      notifier.show(this.config.strings.cartError, "error", 4000);
    } finally {
      this.setAddToCartLoading(false);
      if (!keepOverlay) {
        await this.endStepTransition();
      }
    }
  }

  resetBuilder() {
    this.state = { boxKey: null, boxDesign: null, travel: [], full: [], step: 0 };
    this.lastRemovedSelections = [];
    this.lastRemovedByKind = { travel: [], full: [] };
    this.lastRemovedDesign = null;
    this.stockAdjustedByKind = { travel: false, full: false, design: false };
    this.steps = [];
    Object.values(this.panels).forEach((panel) => panel?.setAttribute("hidden", ""));
    this.panels.size?.removeAttribute("hidden");
    this.stepper?.setAttribute("hidden", "");
    this.sidebar?.setAttribute("hidden", "");
    this.nav?.setAttribute("hidden", "");
    this.querySelector("[data-byob-sidebar-items]") && (this.querySelector("[data-byob-sidebar-items]").innerHTML = "");
    this.querySelectorAll("[data-byob-box]").forEach((button) => button.classList.remove("is-selected"));
    this.updateLayoutMode();
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }
}

if (!customElements.get("byob-builder")) {
  customElements.define("byob-builder", ByobBuilder);
}
